<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';

import { useProjectWrite } from '@/utils/project-write';
import { eqsApi } from '@/api/eqs';
import { useSession } from '@/auth/session';
import BomAutoLinkDrawer from '@/components/BomAutoLinkDrawer.vue';
import BomChangeDrawer from '@/components/BomChangeDrawer.vue';
import BomReleasePanel from '@/components/BomReleasePanel.vue';
import MbomPanel from '@/components/MbomPanel.vue';
import PageState from '@/components/PageState.vue';
import { confirmAction } from '@/services/confirm';
import { notify } from '@/services/notice';
import type { Project } from '@/types/domain';
import { errorMessage } from '@/utils/format';

interface BomNode {
  id: number;
  parentId: number | null;
  /** 来自 Excel 的哪张工作表；类别条按它分组（老数据为 null） */
  sheetName: string | null;
  sheetOrder: number | null;
  level: number;
  /** 有没有零件图片；图本身按需从接口取，列表不背图片体积 */
  hasImage?: boolean;
  partNumber: string;
  partName: string;
  quantity: number;
  totalQuantity: number;
  unit: string;
  material: string | null;
  sourceType: 'make' | 'buy' | 'standard' | 'subcontract';
  revision: string | null;
  supplier: string | null;
  customerPartNumber: string | null;
  process: string | null;
  modelDocumentId: number | null;
  modelTitle: string | null;
  drawingDocumentId: number | null;
  drawingTitle: string | null;
  drawingRevision: string | null;
  note: string | null;
  /* 均胜标准模板剩下的列。默认不显示，勾出来就能填 */
  oldPartNumber: string | null;
  materialGrade: string | null;
  materialSpec: string | null;
  partSpec: string | null;
  mouldNo: string | null;
  netWeightG: number | null;
  grossWeightG: number | null;
  wastageG: number | null;
  dimensions: string | null;
  finish: string | null;
  color: string | null;
  changes: Array<{ id: number; code: string | null; title: string; status: string; open: boolean }>;
  issues: string[];
}

interface BomPayload {
  nodes: BomNode[];
  orphanCount: number;
  summary: {
    total: number; levels: number;
    byType: { make: number; buy: number; standard: number; subcontract: number };
    withDrawing: number; withModel: number; issueCount: number; underChange: number;
  };
  /**
   * EBOM 能不能改，以及该找谁（0827 Tony：EBOM 只能产品工程师改）。
   * 后端随树一起给——前端自己按角色判一遍，迟早和后端的门禁对不上。
   */
  access?: { canEdit: boolean; editors: string[]; reason: string };
}

interface ImportPreview {
  total: number;
  canImport: boolean;
  errors: Array<{ row: number; message: string; blocking?: boolean }>;
  /** 必须先修的处数（会带塌别的行） */
  blockingCount?: number;
  /** 只影响自己、导入时会被跳过的行号 */
  skippedRows?: number[];
  /** 系统替人做掉的事（同件两处已合并数量） */
  notices?: Array<{ row: number; message: string }>;
  items: Array<Record<string, unknown>>;
  sheetNames: Array<{ name: string; looksLikeBom: boolean; rowCount: number }>;
  sheet: string;
  format: 'template' | 'joyson' | 'ai';
  mappingNote: string;
  /**
   * 这次认出了什么（0821 Tony 拍板）：表头原文、字段↔列映射、**没认出来的列**。
   * 最后一项是重点——「某列没映射上」原来从来不报错，
   * 东禾编号在 X04C 文件里 67 行全有值、落库 0 条，界面上那列一直空着。
   */
  revisions?: Array<{
    seq: number | null; version: string | null; content: string | null;
    changedBy: string | null; changedAt: string | null;
  }>;
  source?: {
    headerRow: number;
    headers: string[];
    mapped: Record<string, string>;
    unmapped: string[];
    sheetNames?: string[];
    /** 关键字段取自第几列、怎么认出来的（表头 / 数据长相 / 人工指定） */
    columns?: Array<{
      field: string; label: string; column: number; header: string;
      by: 'header' | 'data' | 'manual';
    }>;
  } | null;
  diff: {
    isReimport: boolean;
    currentTotal?: number;
    added?: number;
    removed?: Array<{ partNumber: string; partName: string; hadFiles: boolean }>;
    removedTotal?: number;
    removedWithFiles?: number;
    quantityChanged?: Array<{ partNumber: string; partName: string; from: number; to: number }>;
    quantityChangedTotal?: number;
    linksKept?: number;
  };
}

const FORMAT_LABEL: Readonly<Record<string, string>> = {
  template: 'PDM 模板', joyson: '均胜标准 BOM', ai: 'AI 识别',
};

const props = defineProps<{ project: Project }>();
const { access } = useSession();
/*
  能不能改 = 账号不是只读 且 这个项目在自己名下。
  所有项目都能看（Tony 2026-08-18 定的口径），收着的只有写。
*/
const canWrite = useProjectWrite(computed(() => props.project));

const data = ref<BomPayload | null>(null);

/**
 * EBOM 的编辑权限（0827 Tony：EBOM 只能由本项目的产品工程师更新）。
 *
 * 在项目写权限之上再收一层：`canWrite` 管「你在不在这个项目组」，
 * 这一层管「你是不是这个项目的产品工程师」。
 * 后端还没带 access 时按能改处理——新前端配旧后端不该把人全锁在外面，
 * 真去写的时候后端那道门照样拦得住。
 */
const canEditEbom = computed(() =>
  canWrite.value && data.value?.access?.canEdit !== false);

/** 不能改 EBOM 时页面上那句话；能改时是空串 */
const ebomLockReason = computed(() =>
  (canWrite.value && data.value?.access?.canEdit === false ? data.value.access.reason : ''));
const loading = ref(true);
const error = ref('');
const savingId = ref<number | null>(null);
const onlyIssues = ref(false);

const importInput = ref<HTMLInputElement | null>(null);
/**
 * 人在预览里手工指定的列对应（字段 → 第几列）。
 *
 * 表头会改名（同一份 X04C 文件三张表就有三个叫法），数据也会有例外，
 * 最后总得有个地方让人说了算——改完立刻按新映射重算一次预览（0821 Tony 拍板）。
 */
const columnOverrides = ref<Record<string, number>>({});
const importPreview = ref<ImportPreview | null>(null);

/*
  异常分两拨看：
  「必须先修」会带塌别的行（下面还有子件 / 零件号重复 / 级别断层），不清零不给导；
  「会跳过」只影响它们自己，允许带着导，但**逐行列出来**——
  静默丢行比报错更危险，这条教训在文件列表上吃过一次。
*/
const blockingErrors = computed(() =>
  (importPreview.value?.errors ?? []).filter((error) => error.blocking));
const skippableErrors = computed(() =>
  (importPreview.value?.errors ?? []).filter((error) => !error.blocking));
const importing = ref(false);
const autoLinkOpen = ref(false);
const changeOpen = ref(false);

/**
 * BOM 更改履历（0822 Tony 第 3 条）。
 * 均胜模板第一张表就是「更改履历表」，以前想看改了什么得把 Excel 下载下来打开。
 * 导入时存进库，这里直接显示。
 */
const revisions = ref<Array<{
  seq: number | null; version: string | null; content: string | null;
  changedBy: string | null; changedAt: string | null;
}>>([]);
const revisionsOpen = ref(false);

async function loadRevisions(): Promise<void> {
  try {
    const payload = await eqsApi.bomRevisions(props.project.id) as {
      revisions?: typeof revisions.value;
    };
    revisions.value = payload.revisions ?? [];
  } catch {
    // 履历读不到不该影响看 BOM 本身
    revisions.value = [];
  }
}

/**
 * 在线改动的履历（0823 Tony 第 1 条：「直接在线修改 BOM 后有修改履历记录」）。
 *
 * 和上面那份**不是一回事**，所以分两块摆：
 * 上面是随 Excel 一起进来的「更改履历表」，工程师自己写的版次说明，重导一次整表替换；
 * 这里是在系统里点出来的改动，谁在什么时候把哪一格从什么改成了什么，只增不改。
 * 合成一块的话，重导 BOM 会把在线改动一起冲掉——而那恰恰是系统唯一的证人。
 */
const edits = ref<Array<{
  partNumber: string | null; partName: string | null; label: string;
  before: string | null; after: string | null;
  actorName: string | null; changedAt: string | null;
}>>([]);
const editsOpen = ref(false);

async function loadEdits(): Promise<void> {
  try {
    const payload = await eqsApi.bomEdits(props.project.id) as { edits?: typeof edits.value };
    edits.value = payload.edits ?? [];
  } catch {
    // 同上：履历读不到不该挡着看 BOM
    edits.value = [];
  }
}

/**
 * 历次导入的识别记录。
 *
 * 这些记录导入时就在写，但界面上从来没有入口。
 * 「东禾QAD号这一列为什么是空的」这类问题，原来只能靠翻当时那份 Excel 回答——
 * 而 Excel 早被下一版覆盖，等于回答不了。unmapped 就是答案本身：
 * 那几列在 Excel 里有值、在 PDM 里没落库。
 */
interface BomImportLog {
  id: number;
  bomType: string | null;
  format: string | null;
  sheetNames: string[];
  headerRow: number | null;
  headers: string[];
  mapped: Record<string, string>;
  unmapped: string[];
  rowCount: number;
  actorName: string | null;
  fileName?: string | null;
  createdAt: string | null;
}

const importLogs = ref<BomImportLog[]>([]);
const importLogsOpen = ref(false);

async function loadImportLogs(): Promise<void> {
  try {
    const payload = await eqsApi.bomImportLogs(props.project.id) as { logs?: BomImportLog[] };
    importLogs.value = payload.logs ?? [];
  } catch {
    // 同更改履历：导入记录读不到不该挡着看 BOM
    importLogs.value = [];
  }
}

/** 后端给的是 ISO 串，原样摆出来没人读得懂；不带时区显示，看的人都在同一个厂 */
function formatImportTime(value: string | null): string {
  if (!value) return '—';
  const time = new Date(value);
  return Number.isNaN(time.getTime())
    ? String(value)
    : time.toLocaleString('zh-CN', { hour12: false });
}

/** 可挂的文件：整个项目的文件清单，模型和图纸都从这里选 */
const documents = ref<Array<{ id: number; code: string | null; title: string }>>([]);

const SOURCE_LABEL: Readonly<Record<string, string>> = {
  make: '自制件', buy: '外购件', standard: '标准件', subcontract: '外协件',
};

/*
  单位（0915 Tony:「你把这个做个下拉选择吧」）。

  原来单位只是件数格子底下的一行小字，界面上**根本改不了**——要改只能重新导一遍
  Excel。于是 0914「把总成 PCS 改成 EA」这件事只能靠脚本全表刷，一刷就刷过了头
  （1504 个子件跟着变了，已还原）。做成下拉之后，谁该是什么单位就地点一下改完，
  不用再动全表。

  列表是常用的那几个，**不是白名单**：`unitOptions()` 会把这一行当前的值也补进去，
  库里已有的写法（g、KG，以及以后有人手填的别的）不会因为不在列表里就被抹掉。
*/
const UNIT_CHOICES = ['EA', 'PCS', 'SET', '套', 'g', 'KG', 'm', 'm²'] as const;

function unitOptions(current: string): string[] {
  const value = (current ?? '').trim();
  return value && !UNIT_CHOICES.includes(value as typeof UNIT_CHOICES[number])
    ? [value, ...UNIT_CHOICES]
    : [...UNIT_CHOICES];
}

/*
  显示哪些列。

  均胜标准 BOM 模板有 23 列，界面原来固定显示 12 列——重量、模具、外形尺寸这些
  在 PDM 上根本填不进去，等于「想在 PDM 上做 BOM」只能做一半。
  但 23 列一起摊开又挤成一团（Tony 8/18 说过一次「都集中在一个页面，很乱很杂」）。
  所以：默认还是那 12 列，要填重量或工艺信息时自己勾出来，选择记在本机。

  勾出来的列和默认列**完全一样**：能改、能用键盘走、能从 Excel 粘一片。

  列名**按 X04C 的 BOM 原词**写（0821 Tony：先要求和 Excel 对齐，再定「以 X04C 为准」）。

  实测「理想X04C_出风口BOM_A12东禾重量更新版_2026.5.27.xlsx」的表头（第 7 行）：
  序号 | 级别 | 件 | 来源 (自制/外购/外协) | 客户零件号 | 均胜编号& 材料QAD号 |
  东禾编号& 材料QAD号 | 零件名称 | 材料名称 | 零件图片 | 净重（g）| 毛重（g）| 损耗（g）|
  外形尺寸 | 材料标准 | 材料牌号 | 材料供应商& 零件供应商 | 零件规格 | 外观 | 客户色号 |
  模具编号 | 加工工艺 | 备注

  和早先对照的那份（RU MCA 中左出风口）**不一样**：X04C 多「零件规格」「模具编号」，
  「原系统编号」写成「东禾编号& 材料QAD号」，「色号」写成「客户色号」。以 X04C 为准。

  留作对照的 RU MCA 表头是
  序号 | 级别 | 件 | 来源(自制/外购/外协) | 客户零件号 | 均胜编号& 材料QAD号 | 原系统编号 |
  零件名称 | 材料名称 | 零件图片 | 净重（g）| 毛重（g）| 损耗（g）| 外形尺寸 |
  材料标准 | 材料牌号 | 材料供应商& 零件供应商 | 外观 | 色号 | 加工工艺 | 备注。
  Excel 里没有的列（累计、3D 模型、2D 图纸、变更、一致性）在提示里写明「Excel 里没有这一列」，
  免得有人对着 Excel 找不到还以为是自己看漏了。
*/
interface BomColumn {
  /** 字段名，同时是 data-cell 的列名和后端接受的字段名 */
  key: string;
  label: string;
  group: string;
  kind: 'text' | 'number' | 'source' | 'photo' | 'total' | 'doc' | 'changes' | 'issues';
  defaultOn: boolean;
  hint?: string;
}

const BOM_COLUMNS: readonly BomColumn[] = [
  /*
    顺序按 2026-08-21 版 X04C 模板从左到右排（0822 Tony 第 2 条：「表头顺序错乱」）。
    Excel 里的次序是：序号|级别|件|来源|主机厂零件号|客户QAD号|东禾QAD号|零件名称|
    材料名称|零件图片|净重|毛重|损耗|外形尺寸|材料标准|材料牌号|材料供应商&零件供应商|
    零件规格|外观|客户色号|模具编号|加工工艺|备注。
    「序号/级别/零件名称」在 PDM 里是第一列那棵树，不单独出列。
    Excel 里没有的（累计、3D/2D、变更、一致性）排在最后，并在提示里写明。
  */
  { key: 'quantity', label: '件数', group: '常用', kind: 'number', defaultOn: true, hint: 'X04C 模板里这一列表头就写一个「件」' },
  { key: 'sourceType', label: '来源', group: '常用', kind: 'source', defaultOn: true, hint: 'Excel 表头是「来源 (自制 / 外购 / 外协)」' },
  { key: 'customerPartNumber', label: '主机厂零件号', group: '常用', kind: 'text', defaultOn: true, hint: '整车厂给的号；老模板里叫「客户零件号」' },
  { key: 'oldPartNumber', label: '东禾QAD号', group: '常用', kind: 'text', defaultOn: true, hint: '东禾内部号 DH…-…；同一份文件里也写作「内部QAD号」' },
  { key: 'material', label: '材料名称', group: '常用', kind: 'text', defaultOn: true },
  { key: 'photo', label: '零件图片', group: '常用', kind: 'photo', defaultOn: true },
  { key: 'netWeightG', label: '净重（g）', group: '重量', kind: 'number', defaultOn: false, hint: '单件净重' },
  { key: 'grossWeightG', label: '毛重（g）', group: '重量', kind: 'number', defaultOn: false, hint: '含流道' },
  { key: 'wastageG', label: '损耗（g）', group: '重量', kind: 'number', defaultOn: false },
  { key: 'dimensions', label: '外形尺寸', group: '工艺与材料', kind: 'text', defaultOn: false },
  { key: 'materialSpec', label: '材料标准', group: '工艺与材料', kind: 'text', defaultOn: false },
  { key: 'materialGrade', label: '材料牌号', group: '工艺与材料', kind: 'text', defaultOn: false },
  { key: 'supplier', label: '供应商', group: '常用', kind: 'text', defaultOn: true, hint: 'Excel 表头是「材料供应商& 零件供应商」' },
  { key: 'partSpec', label: '零件规格', group: '工艺与材料', kind: 'text', defaultOn: false, hint: 'X04C 模板有这一列（Parts grade）' },
  { key: 'finish', label: '外观', group: '工艺与材料', kind: 'text', defaultOn: false, hint: '模板里叫「外观」，填的是表面处理' },
  { key: 'color', label: '客户色号', group: '工艺与材料', kind: 'text', defaultOn: false },
  { key: 'mouldNo', label: '模具编号', group: '工艺与材料', kind: 'text', defaultOn: false, hint: 'X04C 模板有这一列（Mold No.）' },
  { key: 'process', label: '加工工艺', group: '工艺与材料', kind: 'text', defaultOn: false },
  { key: 'note', label: '备注', group: '其他', kind: 'text', defaultOn: false },

  // 以下是 Excel 里没有、PDM 自己的列
  { key: 'revision', label: '版本', group: '常用', kind: 'text', defaultOn: true, hint: 'Excel 里没有这一列：逐行版本，用来核对车间拿的图纸是不是最新版' },
  { key: 'totalQuantity', label: '累计', group: '常用', kind: 'total', defaultOn: true, hint: 'Excel 里没有这一列：件数按层级相乘，PDM 自己算的' },
  { key: 'modelDocumentId', label: '3D 模型', group: '常用', kind: 'doc', defaultOn: true, hint: 'Excel 里没有这一列：PDM 用来挂模型文件' },
  { key: 'drawingDocumentId', label: '2D 图纸', group: '常用', kind: 'doc', defaultOn: true, hint: 'Excel 里没有这一列：PDM 用来挂图纸文件' },
  { key: 'changes', label: '变更', group: '常用', kind: 'changes', defaultOn: true, hint: 'Excel 里没有这一列：这个零件被哪些 ECN 动过' },
  { key: 'issues', label: '一致性', group: '常用', kind: 'issues', defaultOn: true, hint: 'Excel 里没有这一列：PDM 的体检结果' },
];

const COLUMN_STORAGE_KEY = 'eqs.bom.columns.v1';

/** 存不进 localStorage（隐私模式、配额满）不该让 BOM 打不开，一律退回默认 */
function defaultColumns(): Set<string> {
  return new Set(BOM_COLUMNS.filter((column) => column.defaultOn).map((column) => column.key));
}

function loadColumnChoice(): Set<string> {
  try {
    const raw = window.localStorage.getItem(COLUMN_STORAGE_KEY);
    if (!raw) return defaultColumns();
    const known = new Set(BOM_COLUMNS.map((column) => column.key));
    const picked = (JSON.parse(raw) as unknown[])
      .filter((key): key is string => typeof key === 'string' && known.has(key));
    return picked.length ? new Set(picked) : defaultColumns();
  } catch {
    return defaultColumns();
  }
}

const chosenColumns = ref<Set<string>>(loadColumnChoice());
const columnPickerOpen = ref(false);

const activeColumns = computed(() =>
  BOM_COLUMNS.filter((column) => chosenColumns.value.has(column.key)));

const columnGroups = computed(() => {
  const order: string[] = [];
  const grouped = new Map<string, BomColumn[]>();
  for (const column of BOM_COLUMNS) {
    if (!grouped.has(column.group)) { grouped.set(column.group, []); order.push(column.group); }
    grouped.get(column.group)!.push(column);
  }
  return order.map((name) => ({ name, columns: grouped.get(name)! }));
});

function rememberColumns(next: Set<string>): void {
  chosenColumns.value = next;
  try {
    window.localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify([...next]));
  } catch {
    // 存不下就只在这一次会话里生效，不打断操作
  }
}

function toggleColumn(key: string): void {
  const next = new Set(chosenColumns.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  // 一列不剩的表格没有意义，最后一列不让取消
  if (!next.size) return;
  rememberColumns(next);
}

function resetColumns(): void { rememberColumns(defaultColumns()); }

/**
 * EBOM 的排布，和 MBOM 一样按总成分开看。
 *
 * X04C 的 EBOM 有 411 行 4 层，平铺在一页「很乱很杂」（Tony 2026-08-18）——
 * 而 EBOM 的 Excel 本来就是一个变体一张工作表。所以这里也按**顶层总成**切标签，
 * 一次只看一个总成的树。
 *
 * 另外默认**折叠原材料层**：411 行里有一半是塑料粒子/色母，
 * 工程师翻 BOM 是在找零件，材料是点开看的东西，不是挡路的东西。
 */
const activeRoot = ref<number | string | null>(null);
const showMaterials = ref(false);
const search = ref('');

/** 顶层总成 = 没有父件的行 */
const rootNodes = computed(() =>
  (data.value?.nodes ?? []).filter((node) => node.parentId === null));

/** 每个节点属于哪个顶层总成，按树往上找 */
const rootOf = computed(() => {
  const parents = new Map<number, number | null>();
  for (const node of data.value?.nodes ?? []) parents.set(node.id, node.parentId);
  const cache = new Map<number, number>();
  const resolve = (id: number): number => {
    const known = cache.get(id);
    if (known !== undefined) return known;
    const parent = parents.get(id);
    const root = parent === null || parent === undefined ? id : resolve(parent);
    cache.set(id, root);
    return root;
  };
  const map = new Map<number, number>();
  for (const node of data.value?.nodes ?? []) map.set(node.id, resolve(node.id));
  return map;
});

/** 原材料行：塑料粒子、色母、油漆——它们占了一半行数，默认收起来 */
/**
 * 客户零件号列的补位——**已停用**（0821 Tony：「这个零件号识别有问题」）。
 *
 * 0820 加这个是因为当时东禾 QAD 号没有自己的列，材料行看着像"什么号都没有"。
 * 现在「东禾QAD号」是独立一列了，再往客户零件号里补位就是**同一个号显示两遍**，
 * 而且会让人以为客户给的号是 DH25017-… 这种东禾内部号。
 * 客户零件号（8.21 模板叫「主机厂零件号」）空着就是空着，不替它编。
 */
function customerNumberHint(): string {
  return '';
}

function isMaterialRow(node: { partName: string; partNumber: string }): boolean {
  return /塑料粒子|粒子|色母|主剂|固化剂|稀释剂|油漆|清漆/u.test(node.partName)
    || /^4\d{6}(\/|$)/u.test(node.partNumber.trim());
}

/* 量一下还差多少宽才放得下；只用来在列太多时提示，不再驱动顶部拖条 */
const tableWrap = ref<HTMLElement | null>(null);
const overflowWidth = ref(0);

function measureTable(): void {
  const wrap = tableWrap.value;
  if (!wrap) { overflowWidth.value = 0; return; }
  overflowWidth.value = Math.max(0, wrap.scrollWidth - wrap.clientWidth);
}

/*
  类别条按 **Excel 工作表**分组（0820 Tony：「按 BOM 的模板来固化」）。
  均胜标准 BOM 一个变体一张表（Asm_OL / Asm_OR / OL JQ…），条上就该是这几个名字、
  按表在文件里的顺序排。老数据没有 sheet_name（多表导入之前进来的），
  退回原来的「顶层零件各一个标签」。
*/
const hasSheets = computed(() => (data.value?.nodes ?? []).some((node) => node.sheetName));
const rootTabs = computed(() => {
  const nodes = data.value?.nodes ?? [];
  if (hasSheets.value) {
    const seen = new Map<string, { id: string; name: string; order: number; rows: number }>();
    for (const node of nodes) {
      const name = node.sheetName ?? '未记录工作表';
      const tab = seen.get(name)
        ?? { id: `sheet:${name}`, name, order: node.sheetOrder ?? 999, rows: 0 };
      tab.rows += 1;
      seen.set(name, tab);
    }
    return [...seen.values()].sort((a, b) => a.order - b.order);
  }
  return rootNodes.value.map((root) => ({
    id: root.id as number | string,
    name: root.partName || root.partNumber,
    order: 0,
    rows: nodes.filter((node) => rootOf.value.get(node.id) === root.id).length,
  }));
});

/** 当前标签下该显示哪些行：sheet 模式按表名，老模式按顶层归属 */
function inCurrentTab(node: { id: number; sheetName: string | null }): boolean {
  const current = currentRoot.value;
  if (!current) return true;
  if (hasSheets.value) return `sheet:${node.sheetName ?? '未记录工作表'}` === current.id;
  return rootOf.value.get(node.id) === current.id;
}

const currentRoot = computed(() =>
  rootTabs.value.find((item) => item.id === activeRoot.value) ?? rootTabs.value[0] ?? null);

/*
  按模具筛（0911 Tony:「E-BOM&M-BOM 内模具/工装列增加筛选功能，
  方便同模具的产品信息填写」）。

  一套模具做好几个零件，填模具号是按模具一批一批填的，而 BOM 是按结构排的——
  同模具的行散在树的各处。`__blank__` 是「还没填的」那一档，
  挑出来一行行补是这一列最常见的用法。
*/
const MOULD_BLANK = '__blank__';
const mouldFilter = ref('');

function mouldOf(node: { mouldNo?: string | null }): string {
  return (node.mouldNo ?? '').trim();
}

/** 搜索这一维单独判：下拉里的条数要从"没被模具筛过"的那一份算 */
function matchesSearch(node: BomNode): boolean {
  const needle = search.value.trim().toLowerCase();
  if (!needle) return true;
  return `${node.partNumber} ${node.partName} ${node.material ?? ''} ${mouldOf(node)}`
    .toLowerCase().includes(needle);
}

function matchesMould(node: BomNode): boolean {
  const picked = mouldFilter.value;
  if (!picked) return true;
  return picked === MOULD_BLANK ? !mouldOf(node) : mouldOf(node) === picked;
}

/*
  下拉的选项和条数：从**当前标签、过了搜索、但没过模具筛**的那批行算。
  算自己那一维时不带自己的条件——带上的话选中的永远是全部、其余永远是 0
  （0902 审计页栽过这一跤）。
*/
const mouldOptions = computed(() => {
  const counts = new Map<string, number>();
  let blank = 0;
  for (const node of data.value?.nodes ?? []) {
    if (rootTabs.value.length > 1 && !inCurrentTab(node)) continue;
    if (!showMaterials.value && isMaterialRow(node)) continue;
    if (!matchesSearch(node)) continue;
    const code = mouldOf(node);
    if (!code) { blank += 1; continue; }
    counts.set(code, (counts.get(code) ?? 0) + 1);
  }
  const options = [...counts.entries()]
    .map(([value, count]) => ({ value, label: `${value}（${count}）` }))
    .sort((left, right) => left.value.localeCompare(right.value));
  if (blank) options.push({ value: MOULD_BLANK, label: `还没填（${blank}）` });
  return options;
});

const visibleNodes = computed(() => {
  const nodes = data.value?.nodes ?? [];
  const needle = search.value.trim().toLowerCase();
  const filtering = Boolean(needle) || Boolean(mouldFilter.value);
  return nodes.filter((node) => {
    if (onlyIssues.value && !node.issues.length) return false;
    // 只看当前标签（工作表或总成）；只有一个时不做限制，免得空手而归
    if (rootTabs.value.length > 1 && !inCurrentTab(node)) return false;
    if (!showMaterials.value && isMaterialRow(node)) return false;
    // 搜索/筛选时不折叠——否则命中的行可能藏在收起的父件下面
    if (!filtering && isHiddenByCollapse(node)) return false;
    return matchesSearch(node) && matchesMould(node);
  });
});

onMounted(() => {
  measureTable();
  window.addEventListener('resize', measureTable);
});
onBeforeUnmount(() => window.removeEventListener('resize', measureTable));
// 行数/列数变了要重新量：切总成、搜索、改「显示哪些列」都会变宽
watch(() => [visibleNodes.value.length, activeRoot.value, search.value],
  () => void nextTick(measureTable));


/*
  命中在别的总成里时给个跳转，否则搜不到会以为系统里没有。

  判据和上面共用 matchesSearch / matchesMould——同一套模具做的零件本来就常常
  跨总成，这条提示对模具筛选比对搜索更要紧；两边各写一套迟早对不上。
*/
const searchHitsElsewhere = computed(() => {
  if ((!search.value.trim() && !mouldFilter.value) || !currentRoot.value) return [];
  const hit = new Set<number | string>();
  for (const node of data.value?.nodes ?? []) {
    if (inCurrentTab(node)) continue;
    if (matchesSearch(node) && matchesMould(node)) {
      hit.add(hasSheets.value ? `sheet:${node.sheetName ?? '未记录工作表'}` : rootOf.value.get(node.id)!);
    }
  }
  return rootTabs.value.filter((tab) => hit.has(tab.id));
});

/**
 * 折叠。
 *
 * 点总成/父件那一行就把它下面收起来——411 行的 BOM 摊开看不出结构。
 * 记的是「哪些收起来了」而不是「哪些展开了」：默认全展开，
 * 新导入的行不会因为没记录就藏起来。
 */
const collapsed = ref<Set<number>>(new Set());

/** id → 行。改累计时要拿父件的累计来反推数量 */
const nodeById = computed(() => {
  const map = new Map<number, BomNode>();
  for (const node of data.value?.nodes ?? []) map.set(node.id, node);
  return map;
});

/** 父件 → 子件。411 行的 BOM 每渲染一行再全表扫一遍会卡，整表只算一次 */
const childrenOf = computed(() => {
  const map = new Map<number, BomNode[]>();
  for (const node of data.value?.nodes ?? []) {
    if (node.parentId === null) continue;
    const siblings = map.get(node.parentId);
    if (siblings) siblings.push(node);
    else map.set(node.parentId, [node]);
  }
  return map;
});

const parentOf = computed(() =>
  new Map((data.value?.nodes ?? []).map((node) => [node.id, node.parentId])));

/**
 * 这一行展开后**看得到**几行（含孙辈）。
 *
 * 原来只问「有没有子件」，于是收起原材料时每个末端零件都挂着一个箭头，点下去没反应
 * ——Tony 8/19：「有些没有层级的箭头也没有用啊」。
 * 现在按当前视图算：展开真能多出行才给箭头，数字也直接写在提示里。
 */
const expandableCount = computed(() => {
  const counts = new Map<number, number>();
  const children = childrenOf.value;
  const walking = new Set<number>();
  const walk = (id: number): number => {
    const known = counts.get(id);
    if (known !== undefined) return known;
    if (walking.has(id)) return 0;   // 数据成环时不至于把页面转死
    walking.add(id);
    let total = 0;
    for (const child of children.get(id) ?? []) {
      total += (showMaterials.value || !isMaterialRow(child) ? 1 : 0) + walk(child.id);
    }
    counts.set(id, total);
    return total;
  };
  for (const node of data.value?.nodes ?? []) walk(node.id);
  return counts;
});

function canExpand(id: number): boolean {
  return (expandableCount.value.get(id) ?? 0) > 0;
}

function toggleCollapse(id: number): void {
  if (!canExpand(id)) return;
  const next = new Set(collapsed.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  collapsed.value = next;
}

/** 祖先里有任何一个是收起的，这一行就不显示 */
function isHiddenByCollapse(node: { id: number; parentId: number | null }): boolean {
  const parents = parentOf.value;
  let cursor = node.parentId;
  const guard = new Set<number>();
  while (cursor !== null && cursor !== undefined && !guard.has(cursor)) {
    if (collapsed.value.has(cursor)) return true;
    guard.add(cursor);
    cursor = parents.get(cursor) ?? null;
  }
  return false;
}

function collapseAll(): void {
  collapsed.value = new Set((data.value?.nodes ?? [])
    .filter((node) => node.parentId === null).map((node) => node.id));
}
function expandAll(): void { collapsed.value = new Set(); }

/*
  像 Excel 一样编辑。
  目标是让工程师直接在 PDM 上做 BOM——只能导 Excel 的话，同一件事要做两遍，
  没人会用（Tony 2026-08-19）。三件事：所有关键列可改、键盘能走、能从 Excel 粘一片。
*/
/**
 * 可粘贴、可用键盘走的列，顺序就是表格里从左到右的顺序。
 * 跟着「显示哪些列」走：勾出来的列一样能改、能粘——否则勾出来只能看，等于没勾。
 */
const EDIT_KINDS = new Set(['text', 'number', 'source']);
const editableColumns = computed<string[]>(() => [
  'partNumber', 'partName',
  ...activeColumns.value.filter((column) => EDIT_KINDS.has(column.kind)).map((column) => column.key),
]);

const COLUMN_LABEL = computed<Record<string, string>>(() => {
  const labels: Record<string, string> = { partNumber: '零件号', partName: '零件名称' };
  for (const column of BOM_COLUMNS) labels[column.key] = column.label;
  return labels;
});

const SOURCE_ALIASES: Readonly<Record<string, string>> = {
  自制: 'make', 自制件: 'make', m: 'make', make: 'make',
  外购: 'buy', 外购件: 'buy', p: 'buy', buy: 'buy',
  外协: 'subcontract', 外协件: 'subcontract', a: 'subcontract', subcontract: 'subcontract',
  标准: 'standard', 标准件: 'standard', standard: 'standard',
};

/** 焦点移到第 row 行第 col 列的输入框；越界就不动 */
function focusCell(rowIndex: number, column: string): void {
  const selector = `[data-cell="${rowIndex}:${column}"]`;
  const el = document.querySelector<HTMLInputElement | HTMLSelectElement>(selector);
  if (!el) return;
  el.focus();
  // 左右两列钉住了（1031）：按 scroll-padding 再让一下，免得格子停在钉住的列底下
  el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  if (el instanceof HTMLInputElement) el.select();
}

/**
 * 一格里输入的东西 → 后端认的值。
 * 单个格子改和整片粘贴走同一套规则，否则同一个值手打进去成了、粘进去不成。
 */
const WEIGHT_COLUMNS = new Set(['netWeightG', 'grossWeightG', 'wastageG']);

function coerceCell(field: string, raw: string): { ok: boolean; value?: unknown; why?: string } {
  const value = raw.trim();
  if (field === 'quantity') {
    const quantity = Number(value);
    return Number.isFinite(quantity) && quantity > 0
      ? { ok: true, value: quantity } : { ok: false, why: '数量必须是大于 0 的数字。' };
  }
  if (WEIGHT_COLUMNS.has(field)) {
    if (!value) return { ok: true, value: null };
    const weight = Number(value);
    return Number.isFinite(weight) && weight >= 0
      ? { ok: true, value: weight } : { ok: false, why: '重量填数字（克），留空表示还没量。' };
  }
  if (field === 'sourceType') {
    const mapped = SOURCE_ALIASES[value.toLowerCase()] ?? SOURCE_ALIASES[value];
    return mapped ? { ok: true, value: mapped } : { ok: false, why: '来源只认自制件 / 外购件 / 外协件 / 标准件。' };
  }
  if (field === 'partNumber' || field === 'partName') {
    return value ? { ok: true, value } : { ok: false, why: '零件号和名称是这一行的身份，不能空。' };
  }
  return { ok: true, value: value || null };
}

/**
 * 键盘走格子。
 * Enter / ↑↓ 上下同列走，Tab 交给浏览器（它本来就按 DOM 顺序走，正好是左到右）。
 */
function onCellKey(event: KeyboardEvent, rowIndex: number, column: string): void {
  const step = event.key === 'Enter' || event.key === 'ArrowDown' ? 1
    : event.key === 'ArrowUp' ? -1 : 0;
  if (!step) return;
  // 下拉框的上下键是在选项间走，不抢
  if (event.target instanceof HTMLSelectElement && event.key !== 'Enter') return;
  event.preventDefault();
  focusCell(rowIndex + step, column);
}

/**
 * 从 Excel 粘一片区域。
 *
 * 剪贴板里是 TSV：列用制表符、行用换行。从当前格子开始按行列铺开，
 * 超出表格范围的部分丢掉并说一声——**默默少写几行比报错更难查**。
 */
async function onCellPaste(
  event: ClipboardEvent, rowIndex: number, column: string,
): Promise<void> {
  const text = event.clipboardData?.getData('text/plain') ?? '';
  // 单个值走浏览器默认的粘贴，只有多格才接管
  if (!text.includes('\t') && !text.includes('\n')) return;
  event.preventDefault();

  const grid = text.replace(/\r\n?/gu, '\n').replace(/\n$/u, '').split('\n')
    .map((line) => line.split('\t'));
  const columns = editableColumns.value;
  const startCol = columns.indexOf(column);
  const rows = visibleNodes.value;
  const patches: Array<{ id: number; values: Record<string, unknown> }> = [];
  const rejected: string[] = [];
  let droppedRows = 0;
  let droppedCols = 0;

  for (const [rowOffset, cells] of grid.entries()) {
    const node = rows[rowIndex + rowOffset];
    if (!node) { droppedRows += 1; continue; }
    const values: Record<string, unknown> = {};
    for (const [colOffset, raw] of cells.entries()) {
      const field = columns[startCol + colOffset];
      if (!field) { droppedCols += 1; continue; }
      const parsed = coerceCell(field, raw);
      if (!parsed.ok) {
        // 认不出来的格子跳过，但要报出来——默默少写几格比报错更难查
        rejected.push(`第 ${rowIndex + rowOffset + 1} 行 ${COLUMN_LABEL.value[field] ?? field}「${raw.trim()}」`);
        continue;
      }
      values[field] = parsed.value;
    }
    if (Object.keys(values).length) patches.push({ id: node.id, values });
  }

  if (!patches.length) {
    notify('error', '没有粘上', rejected.length
      ? `${rejected.slice(0, 3).join('、')} 没认出来。`
      : '剪贴板里的内容没有对上任何一列。');
    return;
  }
  try {
    const result = await eqsApi.bulkUpdateBom(props.project.id, patches);
    const dropped = [
      droppedRows ? `${droppedRows} 行超出了表格范围` : '',
      droppedCols ? '右边多出来的列被忽略' : '',
      rejected.length ? `${rejected.length} 格没认出来（${rejected.slice(0, 2).join('、')}）` : '',
    ].filter(Boolean).join('；');
    notify('success', `粘上了 ${(result as { changed?: number }).changed ?? patches.length} 行`,
      dropped || '按 Ctrl+Z 没有撤销——改错了直接再粘一次正确的。');
    await load();
  } catch (caught) {
    notify('error', '粘贴没保存上', errorMessage(caught));
  }
}

/** 零件图片：列表只知道有没有，图本身按需从接口取 */
function imageUrl(id: number): string {
  return eqsApi.bomImageUrl(props.project.id, id);
}
const preview = ref<{ url: string; title: string } | null>(null);

/*
  底部直接加一行。
  和问题清单一个思路：加个子件本来要点「+子件」再开抽屉填一堆，
  而现场手上通常只有零件号、名称、数量三样。
*/
const quickRow = ref({ parentId: null as number | null, partNumber: '', partName: '', quantity: '1' });
const quickSaving = ref(false);
const quickReady = computed(() =>
  quickRow.value.partNumber.trim().length > 0 && quickRow.value.partName.trim().length > 0);

/** 可选的父件：当前总成下的所有行，按层级缩进显示 */
const quickParents = computed(() => visibleNodes.value.map((node) => ({
  id: node.id,
  label: `${'　'.repeat(Math.max(0, node.level - 1))}${node.partNumber} ${node.partName}`,
})));

async function submitQuickRow(): Promise<void> {
  if (!quickReady.value || quickSaving.value) return;
  quickSaving.value = true;
  try {
    await eqsApi.createBomItem(props.project.id, {
      parentId: quickRow.value.parentId,
      partNumber: quickRow.value.partNumber.trim(),
      partName: quickRow.value.partName.trim(),
      quantity: Number(quickRow.value.quantity) || 1,
      // 总成（顶层）用 EA，子件用 PCS——和导入那条口径同一条（0915 Tony 的原话）
      unit: quickRow.value.parentId === null ? 'EA' : 'PCS',
      sourceType: 'make',
    });
    notify('success', '已加一行', '来源默认自制件；单位总成给 EA、子件给 PCS——点这一行的单位下拉可以改。');
    quickRow.value = { parentId: quickRow.value.parentId, partNumber: '', partName: '', quantity: '1' };
    await load();
  } catch (caught) {
    notify('error', '加不上', errorMessage(caught));
  } finally {
    quickSaving.value = false;
  }
}

const hiddenMaterialCount = computed(() => {
  if (showMaterials.value || !currentRoot.value) return 0;
  return (data.value?.nodes ?? []).filter((node) =>
    (rootTabs.value.length <= 1 || rootOf.value.get(node.id) === currentRoot.value!.id)
    && isMaterialRow(node)).length;
});

async function load(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    data.value = await eqsApi.bom(props.project.id) as unknown as BomPayload;
  } catch (caught) {
    data.value = null;
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

async function loadDocuments(): Promise<void> {
  try {
    const payload = await eqsApi.projectResourceRecords('documents', 'documents', props.project.id);
    documents.value = (payload.items ?? []).map((item) => ({
      id: Number(item.id),
      code: (item.code as string | null) ?? null,
      title: String(item.title ?? ''),
    }));
  } catch {
    // 取不到文件清单只是没法挂图纸，BOM 本身照常看
    documents.value = [];
  }
}

async function patch(node: BomNode, payload: Record<string, unknown>): Promise<void> {
  savingId.value = node.id;
  try {
    data.value = await eqsApi.updateBomItem(props.project.id, node.id, payload) as unknown as BomPayload;
  } catch (caught) {
    notify('error', `「${node.partNumber}」未保存`, errorMessage(caught));
    await load();
  } finally {
    savingId.value = null;
  }
}

/**
 * 挂模型/图纸从「每行两个下拉」改成「点开再选」。
 *
 * 原来每行两个 <select> 各带全项目文件清单——秦岭 346 个文件 × 42 行 × 2，
 * 近 3 万个 option 节点，页面打开就卡（试用反馈 8/13）。
 * 现在整页只有一个选择器，点到哪行才渲染，还能按编号/名称搜索。
 */
const docPicker = ref<{ node: BomNode; field: 'modelDocumentId' | 'drawingDocumentId' } | null>(null);
const docQuery = ref('');
const DOC_PICKER_LIMIT = 50;
const filteredDocuments = computed(() => {
  const query = docQuery.value.trim().toLowerCase();
  const list = query
    ? documents.value.filter((doc) => `${doc.code ?? ''} ${doc.title}`.toLowerCase().includes(query))
    : documents.value;
  return list.slice(0, DOC_PICKER_LIMIT);
});
const docPickerTruncated = computed(() => {
  const query = docQuery.value.trim().toLowerCase();
  const total = query
    ? documents.value.filter((doc) => `${doc.code ?? ''} ${doc.title}`.toLowerCase().includes(query)).length
    : documents.value.length;
  return Math.max(0, total - DOC_PICKER_LIMIT);
});

function openDocPicker(node: BomNode, field: 'modelDocumentId' | 'drawingDocumentId'): void {
  docPicker.value = { node, field };
  docQuery.value = '';
}

function pickDocument(documentId: number | null): void {
  const target = docPicker.value;
  if (!target) return;
  docPicker.value = null;
  void patch(target.node, { [target.field]: documentId });
}

/*
  行内直传（BUG-011②）。
  原来这里只能从「项目已有文件」里挑，图还在自己电脑上时得先跳到文件页上传、
  再回来找到这一行、再挑一次——工程师手上正拿着这个零件的图，却要走三步
  （试用反馈 0813：「对于复杂产品，分零件多的，可以BOM这里单项上传图纸和模型」）。
  传完直接挂给当前行，文件同样进项目文件库，不是另存一份野文件。
*/
const docUploadInput = ref<HTMLInputElement | null>(null);
const docUploading = ref(false);

async function uploadAndLink(): Promise<void> {
  const target = docPicker.value;
  const file = docUploadInput.value?.files?.[0];
  if (!target || !file) {
    notify('error', '请先选择文件', '选一个要上传的图纸或模型文件。');
    return;
  }
  docUploading.value = true;
  try {
    const payload = new FormData();
    payload.append('file', file);
    // 文件名带上零件号，回头在文件页也认得出这是哪个零件的图
    payload.append('title', `${target.node.partNumber} ${file.name}`);
    const result = await eqsApi.uploadProjectFile(props.project.id, payload);
    const documentId = Number(result.documentId);
    if (!Number.isSafeInteger(documentId) || documentId <= 0) {
      throw new Error('上传成功但没拿到文件 ID，请到文件页确认后手动挂接。');
    }
    const node = target.node;
    const field = target.field;
    docPicker.value = null;
    await patch(node, { [field]: documentId });
    await loadDocuments();
    notify('success', '已上传并挂接', `${file.name} 已挂给 ${node.partNumber}，同时进了项目文件库。`);
  } catch (caught) {
    notify('error', '上传失败', errorMessage(caught));
  } finally {
    docUploading.value = false;
    if (docUploadInput.value) docUploadInput.value.value = '';
  }
}

/** 手工加行（BUG-012）：加同级 = 挂到目标行的父件下，加子件 = 挂到目标行下 */
const addRow = ref<{ parentId: number | null; underName: string } | null>(null);
const addPartNumber = ref('');
const addPartName = ref('');
const addQuantity = ref('1');
const addSource = ref('make');
const addSaving = ref(false);

function openAddRow(parentId: number | null, underName: string): void {
  addRow.value = { parentId, underName };
  addPartNumber.value = '';
  addPartName.value = '';
  addQuantity.value = '1';
  addSource.value = 'make';
}

async function submitAddRow(): Promise<void> {
  if (!addRow.value) return;
  if (!addPartNumber.value.trim() || !addPartName.value.trim()) {
    notify('error', '零件号和名称必填', '这两项是 BOM 行的身份，空着没法建。');
    return;
  }
  const quantity = Number(addQuantity.value);
  if (!Number.isFinite(quantity) || quantity <= 0) {
    notify('error', '数量无效', '数量必须大于 0。');
    return;
  }
  addSaving.value = true;
  try {
    data.value = await eqsApi.createBomItem(props.project.id, {
      parentId: addRow.value.parentId,
      partNumber: addPartNumber.value.trim(),
      partName: addPartName.value.trim(),
      quantity,
      sourceType: addSource.value,
    }) as unknown as BomPayload;
    notify('success', '已添加', `${addPartNumber.value.trim()} ${addPartName.value.trim()}`);
    addRow.value = null;
  } catch (caught) {
    notify('error', '添加失败', errorMessage(caught));
  } finally {
    addSaving.value = false;
  }
}

/**
 * 改「累计」（0821 Tony 要的）。
 *
 * 累计不是库里的一列，它是**算出来的**：累计 = 父件累计 × 本行数量。
 * 所以让它可改，只有一种自洽的做法——按这个恒等式反推数量存回去，
 * 而不是另存一个「累计」字段。存两份就会出现"累计和数量对不上"的数据，
 * 导出到 MBOM、报价、K3 都会各说各话。
 *
 * 父件累计为 0 时反推不出来（0 乘任何数都是 0），这时明确拒绝，
 * 让人先把上层用量填对，而不是悄悄写一个错的数。
 */
function onTotalChange(node: BomNode, event: Event): void {
  const raw = (event.target as HTMLInputElement).value.trim();
  const wanted = Number(raw);
  if (!raw || !Number.isFinite(wanted) || wanted <= 0) {
    notify('error', '累计填得不对', '累计要是一个大于 0 的数。');
    void load();
    return;
  }
  const parentTotal = node.parentId === null
    ? 1
    : nodeById.value.get(node.parentId)?.totalQuantity ?? 0;
  if (!parentTotal) {
    notify('error', '这一行的累计改不了',
      '它的父件累计是 0，反推不出本行数量。先把上层的用量填对。');
    void load();
    return;
  }
  // 反推数量；小数保留 6 位，避免 1/3 这种除不尽的数堆一串
  const quantity = Number((wanted / parentTotal).toFixed(6));
  if (quantity <= 0) {
    notify('error', '累计填得太小', `按父件累计 ${parentTotal} 反推出的数量会是 0。`);
    void load();
    return;
  }
  void patch(node, { quantity });
}

function onFieldChange(node: BomNode, field: string, event: Event): void {
  const raw = (event.target as HTMLInputElement | HTMLSelectElement).value;
  const parsed = coerceCell(field, raw);
  if (!parsed.ok) {
    notify('error', `${COLUMN_LABEL.value[field] ?? field}填得不对`, parsed.why ?? '');
    void load();   // 把格子里的值退回改之前的样子，免得看着像存上了
    return;
  }
  void patch(node, { [field]: parsed.value });
}

/** 一行上某一列当前的值，给输入框用；null 显示成空 */
function cellValue(node: BomNode, key: string): string | number {
  const raw = (node as unknown as Record<string, unknown>)[key];
  if (raw === null || raw === undefined) return '';
  return typeof raw === 'number' ? raw : String(raw);
}

/**
 * 零件名下面那行灰字：客户号和工艺。
 * 这两项也能作为独立列勾出来——勾出来了就不在这儿重复一遍。
 */
function partAside(node: BomNode): string {
  const parts: string[] = [];
  if (node.customerPartNumber && !chosenColumns.value.has('customerPartNumber')) {
    parts.push(`客户号 ${node.customerPartNumber}`);
  }
  if (node.process && !chosenColumns.value.has('process')) parts.push(`工艺 ${node.process}`);
  return parts.join(' · ');
}

async function removeItem(node: BomNode): Promise<void> {
  const childCount = (data.value?.nodes ?? []).filter((item) => item.parentId === node.id).length;
  const confirmed = await confirmAction({
    title: `确认删除「${node.partNumber} ${node.partName}」？`,
    message: childCount
      ? `这一行下面还有 ${childCount} 个子件，会一并删除。`
      : '删除后可以重新导入或手工添加。',
    danger: true,
  });
  if (!confirmed) return;
  try {
    data.value = await eqsApi.removeBomItem(props.project.id, node.id) as unknown as BomPayload;
  } catch (caught) {
    notify('error', '删除失败', errorMessage(caught));
  }
}

/** 改了某个字段取哪一列：记下来，立刻按新映射重算预览 */
async function setColumnOverride(field: string, raw: string): Promise<void> {
  const at = Number(raw);
  if (!Number.isInteger(at)) return;
  columnOverrides.value = { ...columnOverrides.value, [field]: at };
  await previewImport();
}

async function previewImport(sheet?: string): Promise<void> {
  const file = importInput.value?.files?.[0];
  if (!file) {
    notify('error', '请选择文件', '请先选择填好的 BOM Excel。');
    return;
  }
  importing.value = true;
  if (!sheet) importPreview.value = null;
  try {
    const form = new FormData();
    form.append('file', file);
    if (Object.keys(columnOverrides.value).length) {
      form.append('columnOverrides', JSON.stringify(columnOverrides.value));
    }
    importPreview.value = await eqsApi.previewBomImport(props.project.id, form, sheet) as unknown as ImportPreview;
  } catch (caught) {
    notify('error', '解析失败', errorMessage(caught));
  } finally {
    importing.value = false;
  }
}

/**
 * 只把 Excel 里那张「更改履历表」导进来，BOM 行一行不动。
 *
 * 给老项目补课用的：BOM 已经在库里、而且被人工改过，
 * 重导整份会把那些改动冲掉，但履历这份表又确实缺。
 */
async function importRevisionsOnly(): Promise<void> {
  const rows = importPreview.value?.revisions ?? [];
  if (!rows.length || importing.value) return;
  const ok = await confirmAction({
    title: `导入 ${rows.length} 条更改履历？`,
    message: 'BOM 行一行都不会动。这个项目原有的更改履历会被这份文件里的整表替换。',
    confirmLabel: '导入履历',
  });
  if (!ok) return;
  importing.value = true;
  try {
    await eqsApi.importBomRevisions(props.project.id, rows as unknown as Array<Record<string, unknown>>);
    await loadRevisions();
    importPreview.value = null;
    if (importInput.value) importInput.value.value = '';
    revisionsOpen.value = true;
    notify('success', `已导入 ${rows.length} 条更改履历`, 'BOM 行没有改动。');
  } catch (caught) {
    notify('error', '履历没导进去', errorMessage(caught));
  } finally {
    importing.value = false;
  }
}

async function confirmImport(): Promise<void> {
  const preview = importPreview.value;
  if (!preview?.canImport) return;
  const existing = data.value?.summary.total ?? 0;
  const confirmed = await confirmAction({
    title: `确认导入 ${preview.total} 行 BOM？`,
    message: existing
      ? `现有 ${existing} 行将被整表替换。零件号还在的行，已挂的模型与图纸会自动保留。`
      : '导入后可以在表格里继续维护，并挂上模型与图纸。',
    danger: Boolean(existing),
    confirmLabel: '导入并替换',
  });
  if (!confirmed) return;
  importing.value = true;
  try {
    data.value = await eqsApi.confirmBomImport(
      props.project.id, preview.items, preview.source ?? null,
      preview.revisions ?? null) as unknown as BomPayload;
    void loadRevisions();
    void loadImportLogs();
    void loadEdits();
    importPreview.value = null;
    if (importInput.value) importInput.value.value = '';
    notify('success', `已导入 ${preview.total} 行`, '接下来可以给自制件挂上 2D 图纸。');
  } catch (caught) {
    notify('error', '导入失败，未写入', errorMessage(caught));
  } finally {
    importing.value = false;
  }
}

/**
 * EBOM / MBOM 两套 BOM。
 *
 * EBOM 是工程给的零件结构树（这一页原来就是它）；MBOM 是制造用的父子对表，
 * 不手写、从 EBOM 生成。分两个标签而不是并排，是因为两者的列几乎没有重叠——
 * EBOM 有材料/重量/模具/工艺，MBOM 有组织/分子分母/损耗，挤在一张表里谁都看不清。
 */
const bomView = ref<'ebom' | 'mbom'>('ebom');
const locatedEbomId = ref<number | null>(null);
async function locateEbomRow(ref: string): Promise<void> {
  const id = /^E\d+$/.test(ref) ? Number(ref.slice(1)) : NaN;
  const node = data.value?.nodes.find(n => n.id === id);
  if (!node) {
    notify('info', '原始行已变化', '未找到核验引用的工程 BOM 行，请刷新原表并重新核验。');
    return;
  }
  activeRoot.value = hasSheets.value ? `sheet:${node.sheetName ?? '未记录工作表'}` : rootOf.value.get(id) ?? id;
  search.value = ''; mouldFilter.value = ''; onlyIssues.value = false; showMaterials.value = true;
  collapsed.value = new Set(); locatedEbomId.value = id; bomView.value = 'ebom';
  await nextTick();
  document.querySelector(`[data-ebom-row="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

onMounted(() => {
  void load();
  void loadDocuments();
  void loadRevisions();
  void loadImportLogs();
  void loadEdits();
});
</script>

<template>
  <!--
    验收点（1027 复评 r2）：桌面 1440×900、EBOM 默认态（「概况」收起），首屏能看到零件表（原约 995px 才露头）。
    说明条还在老位置，EBOM 下它就是「概况」：说明 · 统计卡 · 次要操作（按文件名挂接 / 关联变更单）收在里面，
    点开原样都在；搜索、全部展开 / 收起、显示哪些列、导入留在原处。不缩正文字号。
    摘要行里的数直接读 data.summary，和展开后的统计卡是同一份。MBOM 下仍是原来那句说明，切标签不跳行。
  -->
  <header class="subpage-context bom-context">
    <details v-if="bomView === 'ebom' && data" class="bom-digest">
      <summary>
        <span class="bom-digest-tag">BOM</span>
        <span v-if="data.summary.total">
          {{ data.summary.total }} 零件行 · {{ data.summary.levels }} 层 · 已挂图纸 {{ data.summary.withDrawing }}
        </span>
        <span v-else>还没有零件</span>
        <span v-if="data.summary.underChange" class="bom-digest-warn">{{ data.summary.underChange }} 个零件变更中</span>
        <span v-if="data.summary.issueCount" class="bom-digest-risk">{{ data.summary.issueCount }} 行有一致性问题</span>
        <em>说明 · 统计 · 更多操作</em>
      </summary>
      <p class="bom-digest-note">
        EBOM 是工程零件结构，MBOM 是制造用的父子对表，由 EBOM 生成。
        数量是「在父件下用几个」；累计用量按层级自动相乘。
        表格里回车 / ↑↓ 上下走格子，可以直接从 Excel 复制一片粘进来。
      </p>
      <section v-if="data.summary.total" class="bom-summary">
        <article><b>{{ data.summary.total }}</b><span>零件行 · {{ data.summary.levels }} 层</span></article>
        <article>
          <b>{{ data.summary.byType.make }}</b>
          <span>
            自制 · 外购 {{ data.summary.byType.buy }} · 外协 {{ data.summary.byType.subcontract }}
            · 标准 {{ data.summary.byType.standard }}
          </span>
        </article>
        <article><b>{{ data.summary.withDrawing }}</b><span>已挂 2D 图纸</span></article>
        <article :class="{ warn: data.summary.underChange > 0 }">
          <b>{{ data.summary.underChange }}</b><span>零件正在变更中</span>
        </article>
        <article :class="{ risk: data.summary.issueCount > 0 }">
          <b>{{ data.summary.issueCount }}</b><span>行有一致性问题</span>
        </article>
      </section>
      <div v-if="canEditEbom && data.summary.total" class="bom-digest-tools">
        <button class="inline-button" type="button" @click="autoLinkOpen = true">按文件名挂接</button>
        <button class="inline-button" type="button" @click="changeOpen = true">关联变更单</button>
      </div>
    </details>
    <p v-else>
      <span>BOM</span>
      EBOM 是工程零件结构，MBOM 是制造用的父子对表，由 EBOM 生成。
    </p>
  </header>

  <!-- 标签和版本条并成一行（1027 复评 r2：「标签页、版本条」原来各占一行）；宽度不够时版本条自己换到下一行 -->
  <div class="bom-head-row">
    <div class="bom-view-tabs" role="tablist">
      <button type="button" role="tab" :aria-selected="bomView === 'ebom'" :class="{ on: bomView === 'ebom' }" @click="bomView = 'ebom'">
        EBOM<small>工程零件结构</small>
      </button>
      <button type="button" role="tab" :aria-selected="bomView === 'mbom'" :class="{ on: bomView === 'mbom' }" @click="bomView = 'mbom'">
        MBOM<small>制造父子对</small>
      </button>
    </div>

    <!-- 发布 EBOM 是 EBOM 上分量最重的动作，跟着产品工程师那道门；MBOM 不在这次口径里 -->
    <BomReleasePanel
      class="bom-head-release"
      :project-id="props.project.id" :bom-type="bomView"
      :can-write="bomView === 'mbom' ? canWrite : canEditEbom"
    />
  </div>

  <MbomPanel
    v-if="bomView === 'mbom'" :project-id="props.project.id" :can-write="canWrite" :can-confirm-pairing="!!data?.access?.canEdit && canWrite"
    @locate-ebom="locateEbomRow" @edit-quantity="bomView = 'ebom'"
  />

  <template v-else>

  <PageState v-if="loading" state="loading" title="正在读取 BOM" message="加载零件结构与文件关联。" />
  <PageState v-else-if="error" state="error" title="BOM 不可用" message="下方是接口返回的原因；重试仍失败请联系管理员。" :detail="error" @retry="load" />

  <template v-else-if="data">
    <p v-if="data.orphanCount" class="bom-orphan">
      有 {{ data.orphanCount }} 行的父件不存在，没有显示在下面的结构里。多半是导入时父件号写错，或父件已被删除。
    </p>

    <!--
      「BILL OF MATERIALS / 零件结构」那层标题拿掉了（1031）：上面已经有 BOM 页签和 EBOM 标签，
      它在 1366×768 下白占 70px。「只看有问题的」挪进表格工具条，跟搜索、筛模具放在一起。
    -->
    <section class="content-panel bom-panel">

      <!--
        EBOM 只能产品工程师改（0827 Tony）。**在页面上先说清楚**，
        而不是等人点了弹一个 403——按钮摆在那里、点下去被拒，是把门禁做成陷阱。
        提示里带着该找谁，人看完就知道下一步去哪。
      -->
      <p v-if="ebomLockReason" class="bom-locked">
        <b>只读</b><span>{{ ebomLockReason }}</span>
      </p>

      <!-- 总成标签：EBOM 的 Excel 本来就是一个变体一张工作表，界面照做 -->
      <nav v-if="rootTabs.length > 1" class="bom-roots" aria-label="选择总成">
        <button
          v-for="tab in rootTabs" :key="tab.id" type="button"
          :class="{ on: tab.id === currentRoot?.id }"
          @click="activeRoot = tab.id"
        >
          <b>{{ tab.name }}</b>
          <em>{{ tab.rows }} 行</em>
        </button>
      </nav>

      <div v-if="data.summary.total" class="bom-toolbar bom-grid-tools">
        <input v-model="search" type="search" placeholder="搜零件号、名称、材料或模具号">
        <!-- 按模具挑一批出来连着填（0911 第 2 页） -->
        <select v-if="mouldOptions.length" v-model="mouldFilter" class="bom-mould-filter">
          <option value="">模具/工装：全部</option>
          <option v-for="option in mouldOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
        <label v-if="data.summary.issueCount" class="bom-filter">
          <input v-model="onlyIssues" type="checkbox"> 只看有问题的 {{ data.summary.issueCount }} 行
        </label>
        <label class="bom-toggle">
          <input v-model="showMaterials" type="checkbox">
          显示原材料<template v-if="hiddenMaterialCount">（收起了 {{ hiddenMaterialCount }} 行）</template>
        </label>
        <button type="button" class="inline-button" @click="collapsed.size ? expandAll() : collapseAll()">
          {{ collapsed.size ? '全部展开' : '全部收起' }}
        </button>

        <!-- 显示哪些列：模板 23 列，默认给常用的，要填重量/工艺时自己勾 -->
        <div class="bom-columns">
          <button
            type="button" class="inline-button bom-columns-toggle"
            :aria-expanded="columnPickerOpen"
            @click="columnPickerOpen = !columnPickerOpen"
          >显示哪些列（{{ activeColumns.length }}/{{ BOM_COLUMNS.length }}）</button>
          <template v-if="columnPickerOpen">
            <div class="bom-columns-mask" @click="columnPickerOpen = false" />
            <div class="bom-columns-panel" role="dialog" aria-label="显示哪些列">
              <header>
                <b>显示哪些列</b>
                <button type="button" class="inline-button" @click="resetColumns">恢复默认</button>
                <button type="button" class="inline-button" @click="columnPickerOpen = false">关闭</button>
              </header>
              <section v-for="group in columnGroups" :key="group.name">
                <b>{{ group.name }}</b>
                <label v-for="column in group.columns" :key="column.key" :title="column.hint">
                  <input
                    type="checkbox" :checked="chosenColumns.has(column.key)"
                    @change="toggleColumn(column.key)"
                  >
                  <span>{{ column.label }}</span>
                </label>
              </section>
              <p>勾出来的列和默认列一样能改、能用键盘走、能从 Excel 粘一片。选择记在这台电脑上。</p>
            </div>
          </template>
        </div>

        <span class="bom-count">{{ visibleNodes.length }} 行</span>
      </div>

      <p v-if="search && searchHitsElsewhere.length" class="bom-jump-hint">
        另有 {{ searchHitsElsewhere.length }} 个总成里也有匹配：
        <button
          v-for="tab in searchHitsElsewhere.slice(0, 5)" :key="tab.id"
          type="button" class="bom-jump" @click="activeRoot = tab.id"
        >{{ tab.name }}</button>
      </p>

      <div
        v-if="visibleNodes.length" ref="tableWrap"
        class="data-table-wrap bom-sticky-scroll"
      >
        <table class="data-table bom-table">
          <thead>
            <tr>
              <!--
                表头按均胜标准 BOM 的写法（0821 Tony：「能不能把 BOM 的表头和 Excel 的表头对齐」）。
                Excel 里零件号和名称是两列（均胜编号& 材料QAD号 / 零件名称），
                界面上合成一列是因为要显示层级缩进——两个词都写出来，对着 Excel 看不迷路。
              -->
              <th class="col-part col-name" title="对应 Excel 的「均胜编号& 材料QAD号」+「零件名称」两列；这里合成一列是为了显示层级">
                均胜编号 / 零件名称
              </th>
              <th
                v-for="column in activeColumns" :key="column.key"
                :class="`col-${column.key}`" :title="column.hint"
              >{{ column.label }}</th>
              <th v-if="canEditEbom" class="col-actions">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(node, rowIndex) in visibleNodes"
              :key="node.id"
              :data-ebom-row="node.id" :class="{ 'has-issue': node.issues.length, saving: savingId === node.id, 'ai-located': locatedEbomId === node.id }"
            >
              <td class="bom-part col-name">
                <div class="bom-part-row" :style="{ paddingLeft: `${(node.level - 1) * 18}px` }">
                  <!--
                    箭头只在真能展开时出现：收起原材料后，末端零件下面已经没有看得见的行，
                    再挂个点不动的箭头只会让人以为坏了（Tony 8/19）。没有子件时留空占位，行首才对得齐。
                  -->
                  <button
                    type="button" class="bom-caret"
                    :class="{ 'is-leaf': !canExpand(node.id), 'is-open': !collapsed.has(node.id) }"
                    :aria-label="collapsed.has(node.id)
                      ? `展开 ${node.partName} 下的 ${expandableCount.get(node.id)} 行`
                      : `收起 ${node.partName} 下的 ${expandableCount.get(node.id)} 行`"
                    :title="canExpand(node.id)
                      ? `${collapsed.has(node.id) ? '展开' : '收起'}下面 ${expandableCount.get(node.id)} 行`
                      : ''"
                    @click="toggleCollapse(node.id)"
                  >{{ canExpand(node.id) ? '▶' : '' }}</button>
                  <div class="bom-part-text">
                    <input
                      v-if="canEditEbom" class="cell-input bom-pn" type="text"
                      :value="node.partNumber" :data-cell="`${rowIndex}:partNumber`"
                      :aria-label="`${node.partNumber} 的零件号`"
                      @change="onFieldChange(node, 'partNumber', $event)"
                      @keydown="onCellKey($event, rowIndex, 'partNumber')"
                      @paste="onCellPaste($event, rowIndex, 'partNumber')"
                    >
                    <b v-else>{{ node.partNumber }}</b>
                    <input
                      v-if="canEditEbom" class="cell-input bom-pname" type="text"
                      :value="node.partName" :data-cell="`${rowIndex}:partName`"
                      :aria-label="`${node.partNumber} 的零件名称`"
                      @change="onFieldChange(node, 'partName', $event)"
                      @keydown="onCellKey($event, rowIndex, 'partName')"
                      @paste="onCellPaste($event, rowIndex, 'partName')"
                    >
                    <span v-else>{{ node.partName }}</span>
                    <!-- 收起来的行要说清藏了什么，否则看着像行没了 -->
                    <small v-if="collapsed.has(node.id)" class="bom-folded">
                      收起了 {{ expandableCount.get(node.id) }} 行
                    </small>
                    <small v-else-if="partAside(node)">{{ partAside(node) }}</small>
                  </div>
                </div>
              </td>

              <td
                v-for="column in activeColumns" :key="column.key"
                :class="`col-${column.key}`"
              >
                <template v-if="column.key === 'photo'">
                  <button
                    v-if="node.hasImage" type="button" class="bom-thumb"
                    :title="`查看 ${node.partName} 的零件图`"
                    @click="preview = { url: imageUrl(node.id), title: `${node.partNumber} ${node.partName}` }"
                  >
                    <img :src="imageUrl(node.id)" :alt="node.partName" loading="lazy">
                  </button>
                  <span v-else class="bom-nophoto">—</span>
                </template>

                <template v-else-if="column.kind === 'total'">
                  <!--
                    累计可改（0821 Tony）：它是「父件累计 × 本行数量」算出来的，
                    改它等于改本行数量——反推后存的是数量，不另存一份累计，
                    否则两个数会各说各话。
                  -->
                  <input
                    v-if="canEditEbom" class="cell-input bom-total-input"
                    type="number" min="0" step="any"
                    :value="node.totalQuantity"
                    :aria-label="`${node.partNumber} 的累计用量`"
                    :title="`累计 = 父件累计 × 数量；改这里会按父件累计反推本行数量`"
                    :data-cell="`${rowIndex}:totalQuantity`"
                    @change="onTotalChange(node, $event)"
                    @keydown="onCellKey($event, rowIndex, 'totalQuantity')"
                  >
                  <span v-else class="bom-total">{{ node.totalQuantity }}</span>
                </template>

                <template v-else-if="column.kind === 'doc'">
                  <button
                    v-if="canEditEbom" type="button" class="doc-pick"
                    :class="{ 'is-empty': node[column.key as 'modelDocumentId' | 'drawingDocumentId'] === null }"
                    :aria-label="`${node.partNumber} 的${column.label}`"
                    :title="(column.key === 'modelDocumentId' ? node.modelTitle : node.drawingTitle) ?? '未挂'"
                    @click="openDocPicker(node, column.key as 'modelDocumentId' | 'drawingDocumentId')"
                  >{{ (column.key === 'modelDocumentId' ? node.modelTitle : node.drawingTitle) ?? '未挂' }}</button>
                  <span v-else>{{ (column.key === 'modelDocumentId' ? node.modelTitle : node.drawingTitle) ?? '—' }}</span>
                </template>

                <template v-else-if="column.kind === 'changes'">
                  <div class="bom-changes">
                    <template v-if="node.changes.length">
                      <span
                        v-for="link in node.changes" :key="link.id"
                        :class="{ 'is-open': link.open }"
                        :title="`${link.title}（${link.status}）`"
                      >{{ link.code || link.title }}</span>
                    </template>
                    <span v-else class="none">—</span>
                  </div>
                </template>

                <template v-else-if="column.kind === 'issues'">
                  <div class="bom-issues">
                    <span v-if="!node.issues.length" class="ok">✓</span>
                    <ul v-else><li v-for="issue in node.issues" :key="issue">{{ issue }}</li></ul>
                  </div>
                </template>

                <template v-else-if="column.kind === 'source'">
                  <select
                    v-if="canEditEbom" class="cell-input" :value="node.sourceType"
                    :aria-label="`${node.partNumber} 的来源`"
                    :data-cell="`${rowIndex}:sourceType`"
                    @change="onFieldChange(node, 'sourceType', $event)"
                    @keydown="onCellKey($event, rowIndex, 'sourceType')"
                    @paste="onCellPaste($event, rowIndex, 'sourceType')"
                  >
                    <option v-for="(label, value) in SOURCE_LABEL" :key="value" :value="value">{{ label }}</option>
                  </select>
                  <span v-else>{{ SOURCE_LABEL[node.sourceType] }}</span>
                </template>

                <template v-else>
                  <input
                    v-if="canEditEbom" class="cell-input"
                    :type="column.kind === 'number' ? 'number' : 'text'"
                    :min="column.key === 'quantity' ? 0.001 : 0"
                    :step="column.key === 'quantity' ? 1 : 'any'"
                    :value="cellValue(node, column.key)"
                    :placeholder="column.key === 'customerPartNumber' ? customerNumberHint() : ''"
                    :aria-label="`${node.partNumber} 的${column.label}`"
                    :data-cell="`${rowIndex}:${column.key}`"
                    @change="onFieldChange(node, column.key, $event)"
                    @keydown="onCellKey($event, rowIndex, column.key)"
                    @paste="onCellPaste($event, rowIndex, column.key)"
                  >
                  <span v-else>{{ cellValue(node, column.key) || (column.key === 'customerPartNumber' ? customerNumberHint() : '') || '—' }}</span>
                  <!-- 单位跟着件数放在同一格（表上本来就是「1 PCS」这样看的），可改时是下拉 -->
                  <select
                    v-if="column.key === 'quantity' && canEditEbom"
                    class="cell-unit" :value="node.unit"
                    :aria-label="`${node.partNumber} 的单位`"
                    @change="onFieldChange(node, 'unit', $event)"
                  >
                    <option v-for="option in unitOptions(node.unit)" :key="option" :value="option">{{ option }}</option>
                  </select>
                  <small v-else-if="column.key === 'quantity'">{{ node.unit }}</small>
                </template>
              </td>

              <td v-if="canEditEbom" class="bom-row-actions">
                <button class="inline-button" type="button" :aria-label="`在 ${node.partNumber} 下加子件`" @click="openAddRow(node.id, `${node.partNumber} 下`)">＋子件</button>
                <button class="inline-button" type="button" :aria-label="`在 ${node.partNumber} 同级加行`" @click="openAddRow(node.parentId, node.parentId === null ? '顶层' : '同级')">＋同级</button>
                <button class="inline-button bom-danger" type="button" :aria-label="`删除 ${node.partNumber}`" @click="removeItem(node)">删除</button>
              </td>
            </tr>
            <!--
              底部直接加一行：加个子件本来要点「＋子件」开抽屉填一堆，
              而现场手上通常只有零件号、名称、数量三样（和问题清单一个思路）。
              这一行不跟着列走——列勾多勾少，加一行要填的永远是这三样。
            -->
            <tr v-if="canEditEbom" class="bom-quick-row">
              <td class="bom-part">
                <div class="bom-quick-cell">
                  <span class="quick-plus">＋ 新一行</span>
                  <select v-model="quickRow.parentId" aria-label="挂在哪个件下面">
                    <option :value="null">（顶层）</option>
                    <option v-for="item in quickParents" :key="item.id" :value="item.id">{{ item.label }}</option>
                  </select>
                </div>
              </td>
              <td :colspan="Math.max(1, activeColumns.length)">
                <div class="bom-quick-fields">
                  <input
                    v-model="quickRow.partNumber" type="text" placeholder="零件号（必填）"
                    class="cell-input" aria-label="新零件的零件号"
                    @keydown.enter="submitQuickRow"
                  >
                  <input
                    v-model="quickRow.partName" type="text" placeholder="零件名称（必填）"
                    class="cell-input" aria-label="新零件的名称"
                    @keydown.enter="submitQuickRow"
                  >
                  <input
                    v-model="quickRow.quantity" type="number" min="0.001" step="1"
                    class="cell-input bom-quick-qty" aria-label="新零件的数量"
                  >
                  <small class="bom-quick-muted">来源默认自制件；单位总成 EA、子件 PCS，加完可在行上改</small>
                </div>
              </td>
              <td class="bom-row-actions">
                <button
                  class="button button-primary bom-quick-save" type="button"
                  :disabled="!quickReady || quickSaving" @click="submitQuickRow"
                >{{ quickSaving ? '保存中…' : '保存这一行' }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!--
        表里一行都没有的四种原因，说清是哪一种。
        原来这块挂在图片预览的 v-else 上，于是 BOM 明明在上面还写着「本项目还没有 BOM」。
      -->
      <div v-else class="bom-empty">
        <template v-if="!data.summary.total">
          <b>本项目还没有 BOM</b>
          <p>用下面的按钮传一份 Excel。没有现成表格就先下载模板，层级列用 1、2、3 表示父子关系，子件写在父件下一行。</p>
        </template>
        <template v-else-if="onlyIssues">
          <b>没有一致性问题</b>
          <p>所有自制件都挂了图纸，版本也对得上。取消勾选「只看有问题的」看全部零件。</p>
        </template>
        <template v-else-if="search.trim()">
          <b>没搜到「{{ search.trim() }}」</b>
          <p>换个关键词，或者清空搜索框。搜索找的是零件号、名称和材料。</p>
        </template>
        <template v-else>
          <b>这个总成下没有可显示的行</b>
          <p v-if="hiddenMaterialCount">下面的 {{ hiddenMaterialCount }} 行都是原材料，勾上「显示原材料」就能看到。</p>
          <p v-else>换一个总成标签看看。</p>
        </template>
      </div>

      <!--
        导入区收到表后面（1027 UI 审计）：天天来这页是查零件、改格子，导入一个项目就那么几回，
        原来整块压在表前面，表要往下翻一屏才看得到。解析预览仍紧跟导入按钮；导入说明默认收起。
      -->
      <div v-if="canWrite" class="bom-toolbar" role="toolbar" aria-label="BOM 导入">
        <!-- 导入是写，跟着产品工程师那道门；导出和模板是读，谁都能用 -->
        <template v-if="canEditEbom">
          <label class="bom-file">
            <span>BOM 文件</span>
            <input ref="importInput" type="file" accept=".xlsx">
          </label>
          <button class="button button-primary" type="button" :disabled="importing" @click="previewImport()">
            {{ importing ? '解析中…' : '解析并预览' }}
          </button>
        </template>
        <a
          v-if="data?.summary.total" class="button button-secondary"
          :href="eqsApi.bomExportUrl(project.id)"
        >导出 BOM</a>
        <a class="button button-secondary bom-template" :href="eqsApi.bomTemplateUrl(project.id)">下载模板</a>
        <!--
          更改履历（0822 Tony 第 3 条）：模板第一张表就是它，
          以前想看这份 BOM 改过什么，得先把 Excel 下载下来打开。
        -->
        <button
          v-if="revisions.length" type="button" class="inline-button bom-rev-toggle"
          @click="revisionsOpen = !revisionsOpen"
        >{{ revisionsOpen ? '收起更改履历' : `更改履历（${revisions.length}）` }}</button>
        <!--
          历次导入：这条按钮是这张记录表唯一的出口。
          没有它，导入时认出了什么、漏认了什么就只写不读，
          等于把证据存进了一个谁都打不开的抽屉。
        -->
        <button
          v-if="importLogs.length" type="button" class="inline-button bom-rev-toggle"
          @click="importLogsOpen = !importLogsOpen"
        >{{ importLogsOpen ? '收起历次导入' : `历次导入（${importLogs.length}）` }}</button>
        <!--
          在线改动（0823 Tony 第 1 条）。单独一个入口，不并进上面的「更改履历」：
          那张表随 Excel 来、重导就整表换掉；这份是系统里点出来的，只增不改。
          放一起的话，重导一次 BOM 会把"谁改了什么"一起冲没。
        -->
        <button
          v-if="edits.length" type="button" class="inline-button bom-rev-toggle"
          @click="editsOpen = !editsOpen"
        >{{ editsOpen ? '收起在线修改' : `在线修改（${edits.length}）` }}</button>
        <details v-if="canEditEbom" class="bom-import-help">
          <summary>导入说明</summary>
          <p class="bom-hint">均胜标准 BOM 直接传即可自动识别；其他格式由 AI 认列，导入前在预览里核对。</p>
        </details>
      </div>

      <section v-if="importPreview" class="bom-preview" :class="{ 'has-error': !importPreview.canImport }">
        <div v-if="importPreview.sheetNames.length > 1" class="bom-sheets">
          <span>工作表：</span>
          <button
            v-for="entry in importPreview.sheetNames" :key="entry.name" type="button"
            :class="{ 'is-active': entry.name === importPreview.sheet, 'is-dim': !entry.looksLikeBom }"
            :disabled="importing"
            @click="previewImport(entry.name)"
          >{{ entry.name }}</button>
        </div>
        <b>
          解析结果（{{ FORMAT_LABEL[importPreview.format] ?? importPreview.format }}）：
          解析出 {{ importPreview.total }} 行<template v-if="blockingErrors.length">，
          <span class="bom-blocking">{{ blockingErrors.length }} 处必须先修</span></template><template
            v-if="skippableErrors.length">，{{ skippableErrors.length }} 行会被跳过</template>
        </b>
        <small v-if="importPreview.format !== 'template'">{{ importPreview.mappingNote }}</small>
        <!--
          表里有、系统没认出来的列（0821 Tony 拍板要显示）。
          这类问题原来完全不吭声：X04C 的「东禾编号」67 行全有值、落库 0 条，
          界面上那一列就一直是空的，谁也不知道为什么。
        -->
        <!--
          列对应表（0821 Tony 拍板）。
          「靠表头认」这条路已经被打脸三次：同一份 X04C 文件的三张工作表，
          第 12 列就写了「东禾QAD号」和「内部QAD号」两个名字，每次都是先丢一批数据才发现。
          所以把"哪个字段取自哪一列"摆在导入之前，认错了当场改。
        -->
        <div v-if="importPreview.source?.columns?.length" class="bom-mapping">
          <b>列对应</b>
          <ul>
            <li v-for="item in importPreview.source.columns" :key="item.field">
              <span>{{ item.label }}</span>
              <select
                :value="item.column"
                :disabled="importing"
                :aria-label="`${item.label}取自哪一列`"
                @change="setColumnOverride(item.field, ($event.target as HTMLSelectElement).value)"
              >
                <option
                  v-for="(head, at) in importPreview.source.headers"
                  :key="at" :value="at"
                >{{ head || `第 ${at + 1} 列` }}</option>
              </select>
              <em :class="`by-${item.by}`">{{
                item.by === 'manual' ? '你指定的' : item.by === 'data' ? '按数据认的' : '按表头认的'
              }}</em>
            </li>
          </ul>
          <small>改完会立刻按新的对应重算一遍预览。表头认不到时系统会按号码长相认（东禾号 DH…-…，客户号 6 位数字）。</small>
        </div>

        <p v-if="importPreview.source?.unmapped?.length" class="bom-unmapped">
          <b>这几列系统没认出来，里面的内容不会导进去：</b>
          <span v-for="name in importPreview.source.unmapped" :key="name">{{ name }}</span>
          <em>需要的话告诉我们加上对应字段。</em>
        </p>
        <p v-else-if="importPreview.source" class="bom-unmapped is-ok">
          ✓ 表里 {{ importPreview.source.headers.length }} 列全部认出来了。
        </p>

        <div v-if="importPreview.diff?.isReimport" class="bom-diff">
          <b>
            与现有 {{ importPreview.diff.currentTotal }} 行相比：
            新增 {{ importPreview.diff.added }} · 删除 {{ importPreview.diff.removedTotal }}
            · 数量变化 {{ importPreview.diff.quantityChangedTotal }}
          </b>
          <ul v-if="importPreview.diff.quantityChanged?.length">
            <li v-for="change in importPreview.diff.quantityChanged.slice(0, 6)" :key="change.partNumber">
              {{ change.partNumber }} {{ change.partName }}：{{ change.from }} → {{ change.to }}
            </li>
          </ul>
          <ul v-if="importPreview.diff.removed?.length">
            <li v-for="gone in importPreview.diff.removed.slice(0, 6)" :key="gone.partNumber">
              删除 {{ gone.partNumber }} {{ gone.partName }}{{ gone.hadFiles ? '（挂过文件）' : '' }}
            </li>
          </ul>
          <small>
            零件号还在的 {{ importPreview.diff.linksKept }} 行，已挂的模型与图纸会自动带过去。
            <template v-if="importPreview.diff.removedWithFiles">
              被删的行里有 {{ importPreview.diff.removedWithFiles }} 行挂过文件，挂接会一并消失。
            </template>
          </small>
        </div>
        <!-- 系统自己处理掉的：合并了同件两处的数量。不是错，但要让人核对 -->
        <template v-if="importPreview.notices?.length">
          <b class="bom-merged">这些行系统已经自动处理：</b>
          <ul>
            <li v-for="item in importPreview.notices.slice(0, 10)" :key="`${item.row}-${item.message}`">
              第 {{ item.row }} 行：{{ item.message }}
            </li>
            <li v-if="importPreview.notices.length > 10">…共 {{ importPreview.notices.length }} 处</li>
          </ul>
        </template>
        <!-- 必须先修的：会带塌别的行（下面还有子件 / 级别断层） -->
        <template v-if="blockingErrors.length">
          <b class="bom-blocking">这些行必须先修，否则会把子件带塌：</b>
          <ul>
            <li v-for="item in blockingErrors.slice(0, 10)" :key="`${item.row}-${item.message}`">
              第 {{ item.row }} 行：{{ item.message }}
            </li>
            <li v-if="blockingErrors.length > 10">…共 {{ blockingErrors.length }} 处</li>
          </ul>
        </template>
        <!-- 只影响自己的：明写跳了谁，不静默丢弃 -->
        <template v-if="skippableErrors.length">
          <b class="bom-skippable">这些行只影响它们自己，导入时会跳过：</b>
          <ul>
            <li v-for="item in skippableErrors.slice(0, 10)" :key="`${item.row}-${item.message}`">
              第 {{ item.row }} 行：{{ item.message }}
            </li>
            <li v-if="skippableErrors.length > 10">…共 {{ skippableErrors.length }} 行</li>
          </ul>
        </template>
        <button
          v-if="importPreview.canImport"
          class="button button-primary"
          type="button"
          :disabled="importing"
          @click="confirmImport"
        >
          确认导入 {{ importPreview.total }} 行<template
            v-if="skippableErrors.length">（跳过 {{ skippableErrors.length }} 行）</template>
        </button>
        <small v-else>请在 Excel 里改正上面「必须先修」的行，然后重新解析。</small>
        <!--
          只导那张「更改履历表」，不动 BOM 行。

          老项目用得上：X04C 那 411 行是从旧系统迁进来的，没走过带履历解析的导入口，
          所以更改履历一条都没有、入口干脆不出现。而整份重导会把其中
          153 行人工改过的数据一起冲掉——这条路让老项目补上履历又不担那个风险。
        -->
        <button
          v-if="importPreview.revisions?.length"
          class="button" type="button" :disabled="importing" @click="importRevisionsOnly"
        >只导更改履历（{{ importPreview.revisions.length }} 条，不动 BOM 行）</button>
      </section>

      <section v-if="revisionsOpen && revisions.length" class="bom-revisions">
        <b>更改履历</b>
        <table class="data-table">
          <thead><tr><th>序号</th><th>版本</th><th>修订内容</th><th>更改人</th><th>更改日期</th></tr></thead>
          <tbody>
            <tr v-for="(item, index) in revisions" :key="index">
              <td>{{ item.seq ?? index + 1 }}</td>
              <td><b>{{ item.version || '—' }}</b></td>
              <td>{{ item.content || '—' }}</td>
              <td>{{ item.changedBy || '—' }}</td>
              <td>{{ item.changedAt || '—' }}</td>
            </tr>
          </tbody>
        </table>
        <small>来自 Excel 的「更改履历表」，导入时一起存下来的。</small>
      </section>

      <section v-if="editsOpen && edits.length" class="bom-revisions">
        <b>在线修改</b>
        <table class="data-table">
          <thead>
            <tr><th>时间</th><th>零件</th><th>改的是</th><th>改之前</th><th>改之后</th><th>谁改的</th></tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in edits" :key="index">
              <td>{{ item.changedAt || '—' }}</td>
              <td>
                <b>{{ item.partNumber || '—' }}</b>
                <template v-if="item.partName"> {{ item.partName }}</template>
              </td>
              <td>{{ item.label }}</td>
              <!-- 空要显示成「空」，摆个「—」会让人以为这条记录本身缺数据 -->
              <td class="bom-edit-was">{{ item.before ?? '空' }}</td>
              <td><b>{{ item.after ?? '空' }}</b></td>
              <td>{{ item.actorName || '—' }}</td>
            </tr>
          </tbody>
        </table>
        <!-- 0824 加了行级动作之后，这句只说「改一格」就不全了——人会以为加行删行没记 -->
        <small>
          在系统里改一格、加一行、删一行都记一条（删除会写明连带删掉几个子件），
          最多留最近 300 条。重新导入 BOM 不会清掉它。
        </small>
      </section>

      <section v-if="importLogsOpen && importLogs.length" class="bom-import-logs">
        <b>历次导入</b>
        <table class="data-table">
          <thead>
            <tr>
              <th>时间</th><th>源文件</th><th>谁导的</th><th>识别成</th><th>工作表</th><th>行数</th>
              <th>没认出来的列</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="log in importLogs" :key="log.id">
              <td>{{ formatImportTime(log.createdAt) }}</td>
              <!-- 哪一份 Excel：同一天导两版 X04C（5.27 / 8.21）列名不一样，只看时间分不清 -->
              <td class="bom-log-file">{{ log.fileName || '—' }}</td>
              <td>{{ log.actorName || '—' }}</td>
              <td>{{ FORMAT_LABEL[log.format ?? ''] ?? log.format ?? '—' }}</td>
              <!-- 表名比数量有用：多表导入时得知道当时到底认了哪几张 -->
              <td>
                <template v-if="log.sheetNames.length">
                  {{ log.sheetNames.length }} 张<em class="bom-log-sheets">{{ log.sheetNames.join('、') }}</em>
                </template>
                <template v-else>—</template>
              </td>
              <td>{{ log.rowCount }}</td>
              <td :class="{ 'has-unmapped': log.unmapped.length > 0 }">
                <span v-if="log.unmapped.length" class="bom-unmapped">
                  <b>{{ log.unmapped.length }} 列没认出来</b>
                  <em>{{ log.unmapped.join('、') }}</em>
                </span>
                <span v-else class="bom-unmapped-none">全部认出</span>
              </td>
            </tr>
          </tbody>
        </table>
        <small>
          「没认出来的列」是指那一列在 Excel 里有内容、但没有对上 PDM 的字段，所以没有落库。
          界面上某一列一直是空的，先看这里。
        </small>
      </section>

      <div v-if="preview" class="bom-preview-mask" @click.self="preview = null">
        <figure class="bom-preview-box">
          <img :src="preview.url" :alt="preview.title">
          <figcaption>
            <span>{{ preview.title }}</span>
            <a :href="preview.url" target="_blank" rel="noopener">在新标签打开</a>
            <button type="button" @click="preview = null">关闭</button>
          </figcaption>
        </figure>
      </div>

      <BomChangeDrawer
        v-if="changeOpen"
        :project-id="project.id"
        :parts="(data.nodes ?? []).map((node) => ({
          id: node.id, level: node.level, partNumber: node.partNumber, partName: node.partName,
        }))"
        @close="changeOpen = false"
        @saved="(payload) => (data = payload as BomPayload)"
      />

      <BomAutoLinkDrawer
        v-if="autoLinkOpen"
        :project-id="project.id"
        @close="autoLinkOpen = false"
        @applied="(payload) => (data = payload as BomPayload)"
      />

      <p class="bom-footnote">
        模型和图纸在「文件」页上传，再回到这里挂给对应零件；文件名带零件号的，用「按文件名挂接」一次挂完。
        <!-- 实际查五项（bom.service.ts:322 起），这句原来只列了三项：漏掉「缺 3D 模型」和「BOM 没填版本」，
             于是那两种红标在页面上找不到出处，人只会以为是系统乱标（0824）。 -->
        「一致性」列检查五件事：末端自制件／外协件是否缺 2D 图纸、是否缺 3D 模型、
        外购与外协件是否填了供应商、图纸版本与 BOM 版本是否一致、BOM 有没有填版本（没填就无从核对）。
        「变更」列显示这个零件被哪几张变更单影响，黄色的是还没关闭的。
      </p>
    </section>

    <!-- 手工加行：零件号+名称+数量+来源，别的字段建完在行内改 -->
    <div v-if="addRow" class="doc-picker-mask" @click.self="addRow = null">
      <div class="doc-picker bom-add-form" role="dialog" aria-label="添加 BOM 行">
        <header>
          <b>添加零件（{{ addRow.underName }}）</b>
          <button type="button" class="inline-button" @click="addRow = null">关闭</button>
        </header>
        <label><span>零件号 *</span><input v-model="addPartNumber" type="text" placeholder="如 710851"></label>
        <label><span>零件名称 *</span><input v-model="addPartName" type="text"></label>
        <label><span>数量</span><input v-model="addQuantity" type="number" min="0.001" step="any"></label>
        <label><span>来源</span>
          <select v-model="addSource">
            <option v-for="(label, value) in SOURCE_LABEL" :key="value" :value="value">{{ label }}</option>
          </select>
        </label>
        <button class="button button-primary" type="button" :disabled="addSaving" @click="submitAddRow">
          {{ addSaving ? '保存中…' : '添加' }}
        </button>
        <p class="doc-picker-more">材料、供应商、版本这些建完后直接在表格里改；挂图纸点行内的「未挂」。</p>
      </div>
    </div>

    <!-- 整页唯一的文件选择器：点到哪行才渲染，替代原来每行两个全量下拉 -->
    <div v-if="docPicker" class="doc-picker-mask" @click.self="docPicker = null">
      <div class="doc-picker" role="dialog" aria-label="选择文件">
        <header>
          <b>选择{{ docPicker.field === 'modelDocumentId' ? '3D 模型' : '2D 图纸' }}</b>
          <small>{{ docPicker.node.partNumber }} {{ docPicker.node.partName }}</small>
          <button type="button" class="inline-button" @click="docPicker = null">关闭</button>
        </header>
        <input
          v-model="docQuery"
          type="search"
          placeholder="输入文件编号或名称筛选"
          autofocus
        />
        <ul>
          <li>
            <button type="button" class="doc-option doc-clear" @click="pickDocument(null)">未挂（清除当前挂接）</button>
          </li>
          <li v-for="doc in filteredDocuments" :key="doc.id">
            <button type="button" class="doc-option" @click="pickDocument(doc.id)">
              {{ doc.code ? `${doc.code} · ` : '' }}{{ doc.title }}
            </button>
          </li>
        </ul>
        <p v-if="docPickerTruncated" class="doc-picker-more">
          还有 {{ docPickerTruncated }} 个文件没有列出，输入关键词缩小范围。
        </p>
        <p v-if="!documents.length" class="doc-picker-more">项目里还没有文件——用下面的「直接上传」把图传上来即可。</p>

        <!-- 图还在自己电脑上时，不用先跳去文件页再回来找这一行 -->
        <div class="doc-upload">
          <b>直接上传并挂给这一行</b>
          <input ref="docUploadInput" type="file" :disabled="docUploading" />
          <button
            type="button"
            class="inline-button"
            :disabled="docUploading"
            @click="uploadAndLink"
          >{{ docUploading ? '上传中…' : '上传并挂接' }}</button>
          <small>文件会同时进项目文件库，标题自动带上零件号 {{ docPicker.node.partNumber }}。</small>
        </div>
      </div>
    </div>
  </template>
  </template>
</template>

<style scoped>
tr.ai-located > td { background: #fff0bb; box-shadow: inset 0 2px #b77900, inset 0 -2px #b77900; }
tr.ai-located { scroll-margin-top: 90px; }
.doc-upload {
  display: grid; gap: 8px; margin-top: 12px; padding: 12px;
  border-top: 1px solid var(--line);
}
.doc-upload b { font-size: 13px; }
.doc-upload input[type=file] { font-size: 12px; }
.doc-upload button { justify-self: start; }
.doc-upload small { color: var(--muted); font-size: 12px; line-height: 1.6; }
.bom-blocking { color: var(--risk); }
.bom-merged { color: var(--blue); }
.bom-skippable { color: #7a4a10; }
.bom-row-actions { white-space: nowrap; }

/* 总成标签：和 MBOM 一个套路，一次只看一个总成的树 */
.bom-roots {
  display: flex; gap: 4px; overflow-x: auto;
  padding-bottom: 2px; margin-bottom: 12px;
  border-bottom: 2px solid var(--line);
}
.bom-roots button {
  display: grid; gap: 1px; flex: 0 0 auto;
  padding: 8px 14px; border: 1px solid var(--line); border-bottom: 0;
  background: var(--surface); color: var(--muted); cursor: pointer; text-align: left;
}
.bom-roots button b { font: 700 12px var(--body); white-space: nowrap; }
.bom-roots button em { font-style: normal; font-size: 11px; }
.bom-roots button.on {
  background: var(--white); color: var(--blue);
  border-color: var(--blue); border-bottom: 2px solid var(--white); margin-bottom: -2px;
}

.bom-toolbar { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin-bottom: 10px; }
.bom-toolbar input[type=search] { min-height: 34px; min-width: 240px; padding: 0 10px; border: 1px solid var(--line); border-radius: 4px; font-size: 12px; }
.bom-mould-filter { padding: 5px 8px; border: 1px solid var(--line); border-radius: 6px; background: var(--surface); }
.bom-toggle { display: inline-flex; align-items: center; gap: 6px; color: var(--muted); font-size: 12px; cursor: pointer; }
.bom-count { margin-left: auto; color: var(--blue); font-size: 12px; font-weight: 800; }
.bom-jump-hint { margin: 0 0 10px; color: var(--muted); font-size: 12px; }
.col-part { min-width: 260px; }
.col-photo { width: 62px; }
.col-revision .cell-input { max-width: 76px; }
/* 单位下拉：平时看着像原来那行小字，鼠标移上去 / 聚焦时才显出边框 */
.cell-unit {
  max-width: 84px; margin-top: 2px; padding: 1px 2px;
  border: 1px solid transparent; border-radius: 3px;
  background: transparent; color: var(--muted); font-size: 11px;
}
.cell-unit:hover { border-color: var(--line); background: var(--white); }
.cell-unit:focus { border-color: var(--blue); background: var(--white); color: var(--ink); outline: none; }
.col-quantity .cell-input, .col-netWeightG .cell-input,
.col-grossWeightG .cell-input, .col-wastageG .cell-input { max-width: 84px; }
.col-actions { white-space: nowrap; }

/*
  显示哪些列。均胜模板 23 列，一起摊开挤成一团，全都藏起来又填不进 PDM，
  所以给个开关，选择记在本机（换台电脑要重勾，不占后端一张表）。
*/
.bom-columns { position: relative; display: inline-flex; }
.bom-columns-toggle[aria-expanded=true] { border-color: var(--blue); color: var(--blue); }
.bom-columns-mask { position: fixed; inset: 0; z-index: 40; }
.bom-columns-panel {
  position: absolute; top: calc(100% + 6px); left: 0; z-index: 41;
  width: min(520px, 86vw); max-height: 60vh; overflow: auto;
  display: grid; gap: 12px; padding: 14px 16px;
  border: 1px solid var(--line); border-radius: var(--radius-s);
  background: var(--white); box-shadow: 0 10px 28px rgba(16, 39, 56, .16);
}
.bom-columns-panel header { display: flex; align-items: center; gap: 10px; }
.bom-columns-panel header b { flex: 1 1 auto; font-size: 14px; }
.bom-columns-panel section { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 14px; }
.bom-columns-panel section > b {
  flex-basis: 100%; color: var(--muted); font: 700 11px var(--body); letter-spacing: .04em;
}
.bom-columns-panel label {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 12px; cursor: pointer; white-space: nowrap;
}
.bom-columns-panel label input { width: 14px; height: 14px; min-height: 0; margin: 0; }
.bom-columns-panel p { margin: 0; color: var(--muted); font-size: 11px; line-height: 1.7; }

/* 折叠：点父件那一行就把它下面收起来 */
.bom-part-row { display: flex; align-items: flex-start; gap: 6px; }
.bom-part-text { min-width: 0; flex: 1 1 auto; }
.bom-folded { color: var(--blue); font-weight: 700; }
.bom-caret {
  flex: 0 0 22px; width: 22px; height: 22px; padding: 0; margin-top: 1px;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid transparent; border-radius: 3px;
  background: transparent; color: var(--blue);
  font-size: 11px; cursor: pointer; line-height: 1;
  transition: transform .12s ease, background .12s ease;
}
.bom-caret:hover { border-color: var(--line); background: var(--surface); }
/* 展开时箭头朝下——一眼看得出这一行是开着还是收着 */
.bom-caret.is-open { transform: rotate(90deg); }
.bom-caret.is-leaf { cursor: default; }
.bom-caret.is-leaf:hover { border-color: transparent; background: transparent; }

/* 第一列的零件号和名称也能直接改——只能导 Excel 的话，同一件事要做两遍 */
.bom-pn { font-weight: 800; }
.bom-pn, .bom-pname { display: block; width: 100%; margin-bottom: 2px; }
.bom-pname { color: var(--muted); }

/* 零件图片：不用点开就知道是哪个件 */
.bom-thumb {
  width: 46px; height: 46px; padding: 0;
  border: 1px solid var(--line); background: var(--paper);
  overflow: hidden; cursor: zoom-in;
}
.bom-thumb img { width: 100%; height: 100%; object-fit: contain; display: block; }
.bom-thumb:hover { border-color: var(--blue); }
.bom-nophoto { color: var(--muted); }

.bom-preview-mask {
  position: fixed; inset: 0; z-index: 60;
  display: grid; place-items: center; padding: 24px;
  background: rgba(16, 39, 56, .72);
}
.bom-preview-box { max-width: min(900px, 92vw); max-height: 90vh; margin: 0; display: grid; gap: 8px; }
.bom-preview-box img { max-width: 100%; max-height: 78vh; object-fit: contain; background: var(--white); }
.bom-preview-box figcaption { display: flex; align-items: center; gap: 12px; color: white; font-size: 13px; }
.bom-preview-box figcaption span { flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bom-preview-box a { color: var(--cyan); }
.bom-preview-box button { padding: 4px 12px; border: 1px solid rgba(255,255,255,.4); background: transparent; color: white; cursor: pointer; }

/* 底部加一行：一眼看得出是还没保存的新行 */
.bom-quick-row td { background: #fbfcfe; border-top: 2px dashed var(--line); vertical-align: middle; }
.bom-quick-cell { display: flex; align-items: center; gap: 8px; }
.bom-quick-cell select { flex: 1 1 auto; min-width: 0; min-height: 30px; border: 1px solid var(--line); border-radius: 4px; font-size: 12px; }
.quick-plus { color: var(--blue); font-size: 12px; font-weight: 800; white-space: nowrap; }
/* 三个输入框排一行：列勾多勾少，加一行要填的永远是零件号、名称、数量 */
.bom-quick-fields { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.bom-quick-fields .cell-input { flex: 1 1 140px; min-width: 120px; border-color: #c7d5e2; background: var(--white); }
.bom-quick-qty { flex: 0 0 72px; min-width: 72px; }
/* 提示固定另起一行：勾几列都长一个样，不会时而挤在输入框后面时而掉下来 */
.bom-quick-muted { flex: 1 1 100%; color: var(--muted); font-size: 11px; }
.bom-quick-save { white-space: nowrap; }

.bom-jump {
  margin-left: 6px; padding: 2px 8px; border: 1px solid var(--line);
  background: var(--white); color: var(--blue); font: inherit; font-size: 12px; cursor: pointer;
}
.bom-row-actions .inline-button { margin-right: 6px; }

/* EBOM / MBOM 切换：两套 BOM 的列几乎不重叠，分标签比并排清楚 */
.bom-view-tabs { display: flex; gap: 6px; margin-bottom: 14px; }
/* 1027 复评 r2：说明字挪到标签名右边（原来压在下面），标签和版本条一样高、并在一行 */
.bom-view-tabs button {
  display: inline-flex; align-items: baseline; gap: 6px; padding: 9px 18px;
  border: 1px solid var(--line); background: var(--white);
  color: var(--muted); font: 800 14px var(--display); cursor: pointer; text-align: left;
}
.bom-view-tabs button small { color: var(--muted); font: 400 11px var(--body); }
.bom-view-tabs button.on { border-color: var(--blue); border-width: 2px; color: var(--blue); background: rgba(0, 117, 174, .07); }
.bom-head-row { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 8px 12px; margin-bottom: 14px; }
.bom-head-row > .bom-view-tabs { margin-bottom: 0; }
.bom-head-row > .bom-head-release { flex: 1 1 360px; min-width: 0; margin-bottom: 0; }
.bom-danger { color: var(--risk); }
.bom-add-form { max-width: 420px; grid-template-rows: none; }
.bom-add-form label { display: grid; gap: 4px; font-size: 12px; color: var(--muted); }
.bom-add-form label input, .bom-add-form label select { padding: 7px 10px; border: 1px solid var(--line); border-radius: 4px; font-size: 13px; }
.doc-pick {
  max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  padding: 4px 8px; border: 1px solid var(--line); border-radius: 4px;
  background: var(--white); color: var(--ink); font-size: 12px; cursor: pointer; text-align: left;
}
.doc-pick:hover { border-color: var(--blue); }
.doc-pick.is-empty { color: var(--muted); border-style: dashed; }
.doc-picker-mask {
  position: fixed; inset: 0; z-index: 60; display: grid; place-items: center;
  background: rgba(20, 30, 40, .35);
}
.doc-picker {
  display: grid; gap: 10px; width: min(560px, 92vw); max-height: 76vh;
  padding: 16px; border-radius: var(--radius-s); background: var(--white);
  grid-template-rows: auto auto 1fr auto;
}
.doc-picker header { display: flex; align-items: baseline; gap: 10px; }
.doc-picker header b { font-size: 14px; }
.doc-picker header small { flex: 1; color: var(--muted); font-size: 12px; }
.doc-picker input { padding: 7px 10px; border: 1px solid var(--line); border-radius: 4px; font-size: 13px; }
.doc-picker ul { margin: 0; padding: 0; overflow: auto; list-style: none; display: grid; gap: 2px; }
.doc-option {
  display: block; width: 100%; padding: 6px 9px; border: 0; border-radius: 4px;
  background: transparent; color: var(--ink); font-size: 12px; text-align: left; cursor: pointer;
}
.doc-option:hover { background: rgba(0, 117, 174, .08); }
.doc-clear { color: var(--muted); }
.doc-picker-more { margin: 0; color: var(--muted); font-size: 12px; }

.bom-summary { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 10px; margin-bottom: 12px; }
.bom-summary article { display: grid; gap: 2px; padding: 12px 14px; border: 1px solid var(--line); border-left: 3px solid var(--blue); border-radius: var(--radius-s); background: var(--white); }
.bom-summary article.risk { border-left-color: var(--risk); }
.bom-summary article.risk b { color: var(--risk); }
.bom-summary b { font: 700 24px var(--display); }
.bom-summary span { color: var(--muted); font-size: 12px; }
/*
  「概况」（1027 复评 r2）：EBOM 下说明条本身就是可展开的摘要，收起时和原来的说明条一样高；
  展开后是原来的说明、五张统计卡和两个次要操作。字号照说明条原样，不缩。
*/
.bom-context > .bom-digest { flex: 1 1 auto; align-self: stretch; min-width: 0; }
.bom-digest > summary {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap; min-height: 36px;
  color: var(--muted); font-size: 13px; line-height: 1.5; cursor: pointer;
}
.bom-digest-tag { color: var(--blue); font: 800 13px var(--display); letter-spacing: .12em; }
.bom-digest > summary em { margin-left: auto; color: var(--blue); font-style: normal; font-size: 12px; white-space: nowrap; }
.bom-digest > summary em::after { content: ' ▾'; }
.bom-digest[open] > summary em::after { content: ' ▴'; }
.bom-digest-warn { color: #8a5a00; font-weight: 800; }
.bom-digest-risk { color: var(--risk); font-weight: 800; }
.bom-digest-note { margin: 4px 0 0; color: var(--muted); font-size: 13px; line-height: 1.8; }
.bom-digest .bom-summary { margin: 10px 0; }
.bom-digest-tools { display: flex; gap: 12px; padding-bottom: 4px; }

.bom-orphan { margin: 0 0 12px; padding: 9px 12px; border-left: 3px solid var(--warning-graphic); background: #fdf6ec; color: #7a4a10; font-size: 12px; line-height: 1.7; }
/* EBOM 只读横幅（0827）：蓝色说明条，不是报错——这不是出了问题，是这份数据有主 */
.bom-locked {
  display: flex; align-items: baseline; gap: 8px;
  margin: 0 0 12px; padding: 9px 12px;
  border-left: 3px solid var(--blue); background: rgba(0, 117, 174, .06);
  color: var(--ink); font-size: 12px; line-height: 1.7;
}
.bom-locked > b { color: var(--blue); white-space: nowrap; }
/* 复选框默认继承 input 的 min-height:42，比旁边按钮高一截，行内就会错位 */
.bom-filter { display: inline-flex; align-items: center; gap: 6px; min-height: 34px; color: var(--risk); font-size: 12px; font-weight: 700; cursor: pointer; }
.bom-filter input { width: 15px; height: 15px; min-height: 0; margin: 0; }

.bom-toolbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin: 16px 0 14px; padding: 14px 16px; border: 1px solid var(--line); border-radius: var(--radius-s); background: var(--paper); }
/* 三个控件统一高度：文件框、主按钮、次按钮站同一条基线，不再高高低低 */
.bom-file { display: flex; align-items: center; gap: 10px; min-height: 38px; color: var(--muted); font-size: 13px; }
.bom-file > span { white-space: nowrap; font-weight: 700; }
.bom-file input { max-width: 300px; padding: 7px 9px; border: 1px solid var(--line); border-radius: var(--radius-s); background: var(--white); font: inherit; font-size: 12px; }
.bom-toolbar .button { flex: none; }
/* 表格上方那条工具条（1027 复评 r2）：上面那条 .bom-toolbar 是给导入区的大框，表格工具条不套它的留白 */
.bom-toolbar.bom-grid-tools { margin: 10px 0; padding: 8px 12px; }
/* 三个控件成组靠左：宽屏下把「下载模板」推到右端，中间会空出几百像素，看着像两拨东西 */
.bom-template { margin-left: 0; }
.bom-hint { flex-basis: 100%; margin: 0; color: var(--muted); font-size: 12px; line-height: 1.7; }
/* 导入说明折起来占一行；summary 只按字宽，点旁边空白不会误开 */
.bom-import-help { flex-basis: 100%; color: var(--muted); font-size: 12px; }
.bom-import-help > summary { width: max-content; cursor: pointer; }
.bom-import-help .bom-hint { margin-top: 4px; }
.bom-edit-was { color: var(--muted); text-decoration: line-through; }
.bom-revisions { margin: 10px 0; padding: 10px 12px; border: 1px solid var(--line); border-radius: 10px; background: var(--white); }
.bom-revisions > b { font-size: 13px; }
.bom-revisions table { margin-top: 8px; }
.bom-revisions small { display: block; margin-top: 6px; color: var(--muted); font-size: 11px; }
.bom-rev-toggle { margin-left: 8px; }
.bom-import-logs { margin: 10px 0; padding: 10px 12px; border: 1px solid var(--line); border-radius: 10px; background: var(--white); }
.bom-import-logs > b { font-size: 13px; }
.bom-import-logs table { margin-top: 8px; }
.bom-import-logs small { display: block; margin-top: 6px; color: var(--muted); font-size: 11px; line-height: 1.6; }
.bom-log-sheets { display: block; font-style: normal; color: var(--muted); font-size: 11px; }
/*
  「没认出来的列」是这张表唯一非看不可的一格：它对应的是
  「Excel 里那列有值、PDM 里那列全空」。混在普通单元格里没人会注意到，
  所以整格上色、数量单独标红，让人一眼扫到哪次导入漏了东西。
*/
.bom-import-logs td.has-unmapped { background: #fff4e5; }
.bom-unmapped { display: grid; gap: 2px; }
.bom-unmapped b { color: var(--risk); font-size: 12px; }
.bom-unmapped em { font-style: normal; color: #7a4a10; font-size: 11px; line-height: 1.5; }
.bom-unmapped-none { color: var(--muted); font-size: 11px; }
.bom-mapping { margin: 8px 0 0; padding: 9px 11px; border: 1px solid var(--line); border-radius: 8px; background: var(--white); }
.bom-mapping > b { font-size: 12px; }
.bom-mapping ul { display: flex; flex-wrap: wrap; gap: 8px 14px; margin: 7px 0 6px; padding: 0; list-style: none; }
.bom-mapping li { display: flex; align-items: center; gap: 5px; font-size: 12px; }
.bom-mapping select { max-width: 170px; font-size: 12px; }
.bom-mapping em { font-style: normal; font-size: 11px; color: var(--muted); }
.bom-mapping em.by-data { color: #d2961e; }
.bom-mapping em.by-manual { color: var(--blue); font-weight: 700; }
.bom-mapping small { color: var(--muted); font-size: 11px; line-height: 1.6; }
.bom-unmapped { display: flex; align-items: baseline; flex-wrap: wrap; gap: 6px; margin: 8px 0 0; padding: 8px 10px; border-radius: 8px; background: rgba(210, 150, 30, .1); font-size: 12px; }
.bom-unmapped span { padding: 1px 8px; border-radius: 999px; background: #fff; border: 1px solid var(--line); }
.bom-unmapped em { font-style: normal; color: var(--muted); }
.bom-unmapped.is-ok { background: rgba(46, 158, 107, .1); }
.bom-preview { display: grid; gap: 8px; margin-bottom: 14px; padding: 12px 14px; border: 1px solid var(--line); border-left: 3px solid var(--green); border-radius: var(--radius-s); background: var(--paper); }
.bom-preview.has-error { border-left-color: var(--risk); }
.bom-preview b { font-size: 13px; }
.bom-preview ul { margin: 0; padding-left: 18px; color: var(--risk); font-size: 12px; line-height: 1.8; }
.bom-preview small { color: var(--muted); font-size: 12px; line-height: 1.8; }
.bom-diff { display: grid; gap: 6px; padding: 10px 12px; border: 1px solid var(--line); border-radius: var(--radius-s); background: var(--white); }
.bom-diff b { font-size: 12px; }
.bom-diff ul { margin: 0; padding-left: 18px; color: var(--ink); font-size: 12px; line-height: 1.8; }

/*
  不再用 min-width 硬撑到 1180px——那是「一定会横向溢出」的写法。
  改成按内容自适应 + 各列压紧，常规 17 列在 1366 笔记本上就能放下；
  真放不下时容器自己出横向滚动条（在框底边上，手不用离开表格）。
*/
.bom-table { width: 100%; table-layout: auto; }
/*
  表格自己滚，不再把整页撑高（0820 Tony：「右侧做一个滑动的功能，
  不用拖动整个页面，直接拖动表格就行」）。

  改之前：X04C 的表容器高 4328px，页面被撑到 5600+px，
  竖向要拖整页、横向的滚动条还落在页面最底下。
  现在框住高度 → 右侧出现表格自己的竖向滚动条，底部是横向的，
  两根都在框边上，手就在表格上不用离开。

  表头必须同时粘住：滚到第 40 行还得知道哪列是「净重」哪列是「毛重」。
*/
.bom-sticky-scroll {
  scrollbar-gutter: stable;
  /* 减掉的是页头 + Gate 条 + 统计卡 + 工具栏的大致高度；
     用 vh 而不是写死 px，笔记本和大屏都能占满一屏又不溢出 */
  max-height: calc(100vh - 260px);
  min-height: 320px;
  overflow: auto;
  border: 1px solid var(--line);
  border-radius: var(--radius-s);
}
.bom-sticky-scroll thead th {
  position: sticky; top: 0; z-index: 2;
  /* 表头背景必须实心：透明的话滚上来的行会从字底下透出来 */
  background: var(--paper-2);
}

/*
  四根宽列压住，常规 17 列就能在 1366 笔记本上放下（实测这四列原本吃掉 912px）：
    零件号/名称 396 · 操作 192 · 3D 模型 162 · 2D 图纸 162
  文件名超长的用省略号，鼠标悬停仍看得到全名（title 已经带了）。
*/
.bom-table th:first-child, .bom-part { min-width: 150px; max-width: 210px; }
.bom-table th, .bom-table td { padding-left: 5px; padding-right: 5px; }
.bom-table .cell-input { min-width: 56px; }
.bom-table select.cell-input { max-width: 132px; }
/*
  3D/2D 两列被长文件名撑到 162px。
  给 <td> 写 max-width 在 table-layout:auto 下**不生效**（浏览器按内容算列宽），
  必须限制**单元格内的元素**——这里就是 .doc-pick 那个按钮。
  截断后全名仍在 title 里，鼠标停上去看得到。
*/
.bom-table .doc-pick,
.bom-table td .doc-pick + span,
.bom-table .col-model span, .bom-table .col-drawing span {
  display: inline-block; max-width: 72px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle;
}
/* 操作列的三个按钮压成一行小按钮 */
.col-actions, .bom-table td:last-child { white-space: nowrap; }
.bom-table td:last-child .inline-button { padding: 0 6px; font-size: 11px; }
.bom-table tbody tr.has-issue { background: rgba(181, 56, 49, .05); }
.bom-table tbody tr.saving { opacity: .55; }
/*
  只管零件号/名称那一叠，不要写成 .bom-part div——那会把外层的
  .bom-part-row 也变成 grid，箭头就被挤到零件号上面单独一行去了。
*/
.bom-part-text { display: grid; gap: 1px; }
.bom-part b { font-size: 13px; }
.bom-part span { color: var(--muted); font-size: 12px; }
.bom-part small { color: var(--muted); font-size: 12px; opacity: .8; }

.bom-sheets { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; font-size: 12px; color: var(--muted); }
.bom-sheets button { padding: 3px 9px; border: 1px solid var(--line); background: var(--white); font-size: 12px; cursor: pointer; }
.bom-sheets button.is-active { border-color: var(--blue); color: white; background: var(--blue); }
.bom-sheets button.is-dim:not(.is-active) { opacity: .55; }
.bom-total { color: var(--muted); font-variant-numeric: tabular-nums; }
.bom-issues .ok { color: var(--green); font-weight: 800; }
.bom-summary article.warn { border-left-color: var(--warning-graphic); }
.bom-changes { display: flex; flex-wrap: wrap; gap: 4px; font-size: 12px; }
.bom-changes span { padding: 1px 6px; border: 1px solid var(--line); border-radius: 999px; color: var(--muted); }
.bom-changes span.is-open { border-color: var(--warning-graphic); color: #7a4a10; background: #fdf6ec; }
.bom-changes .none { border: 0; padding: 0; }
.bom-issues ul { margin: 0; padding-left: 15px; color: var(--risk); font-size: 12px; line-height: 1.6; }

.cell-input { width: 100%; min-width: 74px; padding: 4px 6px; border: 1px solid transparent; background: transparent; font: inherit; font-size: 12px; }
.cell-input:hover { border-color: #c7d5e2; background: var(--white); }
.cell-input:focus { border-color: var(--blue); background: var(--white); outline: none; }

.bom-empty { display: grid; gap: 8px; justify-items: start; padding: 26px 4px; }
.bom-empty b { font-size: 15px; }
.bom-empty p { margin: 0; max-width: 68ch; color: var(--muted); font-size: 13px; line-height: 1.8; }
.bom-footnote { margin: 14px 0 0; padding-top: 12px; border-top: 1px solid var(--line); color: var(--muted); font-size: 12px; line-height: 1.9; }

/*
  BOM 首屏（1031 Tony：「先改项目页和 BOM 首屏」）。只动桌面（>900px），手机保持原样。

  1366×768 实测：第一行数据在 815px，一行 137px，267 行的表一屏看 4 行。
  行高是叠出来的：styles.css 给所有 input/select 一条 min-height:42（照顾手机手指），
  表格单元格又是零件号、名称、件数、单位上下叠着的输入框——42 × 2 + 小字 + 上下 14px 留白 = 137。
  表格里的格子是 Excel 那种格子，不是表单：压回 28px，零件号、名称、工艺并成一行，单位跟在件数后面。

  表比容器宽时要横向滚，滚过去就不知道是哪个件、也点不到操作。零件列钉在左边，操作列钉在右边。
  钉住的格子背景必须实心，否则横滚时后面的字会透出来；有问题的行、AI 定位的行各补一条实心底色。
*/
@media (min-width: 901px) {
  .bom-context { min-height: 40px; margin-bottom: 8px; padding-block: 2px; }
  .bom-digest > summary { min-height: 34px; }
  .bom-head-row { margin-bottom: 8px; }
  .bom-view-tabs button { padding: 6px 14px; }
  .content-panel.bom-panel { padding: 12px 14px 16px; }
  .bom-roots { margin-bottom: 8px; }
  .bom-roots button { display: inline-flex; align-items: baseline; gap: 6px; padding: 6px 12px; }
  .bom-toolbar.bom-grid-tools { margin: 0 0 8px; padding: 6px 10px; }
  .bom-mould-filter { min-height: 34px; }

  .bom-table td { padding-top: 4px; padding-bottom: 4px; vertical-align: middle; }
  .bom-table .cell-input,
  .bom-table .cell-unit { height: 28px; min-height: 0; }
  .bom-table th:first-child, .bom-part { min-width: 300px; max-width: 380px; }
  .bom-part-row { align-items: center; }
  .bom-part-text { display: flex; align-items: center; gap: 6px; }
  .bom-pn { flex: 0 0 auto; width: 7.5em; min-width: 0; margin: 0; }
  .bom-pname { flex: 1 1 auto; min-width: 6em; margin: 0; }
  .bom-part-text > b { flex: none; }
  .bom-part-text > span,
  .bom-part-text > small { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .bom-part-text > span { flex: 1 1 auto; }
  /* 「工艺 注塑+喷漆」这类短注不跟名称抢：整段露出来，太长才截 */
  .bom-part-text > small { flex: 0 0 auto; max-width: 9em; }
  /* 一致性那列原来只有 46px，「自制件没有挂 2D 图纸」一个字一行，单行撑到 300px */
  .bom-issues ul { min-width: 11em; line-height: 1.4; }
  .bom-thumb { width: 40px; height: 28px; }
  .bom-table td.col-quantity { white-space: nowrap; }
  .bom-table .col-quantity .cell-input { display: inline-block; width: 48px; min-width: 0; vertical-align: middle; }
  .bom-table .cell-unit { margin: 0 0 0 2px; vertical-align: middle; }
  .bom-table td.col-quantity small { margin-left: 4px; }

  .bom-sticky-scroll { scroll-padding: 44px 170px 0 310px; }
  .bom-sticky-scroll .bom-table th:first-child,
  .bom-sticky-scroll .bom-table td.bom-part {
    position: sticky; left: 0; z-index: 1;
    background: var(--white); box-shadow: inset -1px 0 var(--line);
  }
  .bom-sticky-scroll .bom-table th.col-actions,
  .bom-sticky-scroll .bom-table td.bom-row-actions {
    position: sticky; right: 0; z-index: 1;
    background: var(--white); box-shadow: inset 1px 0 var(--line);
  }
  /* 两个角上的表头格同时粘顶和粘边，要压在两边的格子上面 */
  .bom-sticky-scroll .bom-table thead th:first-child,
  .bom-sticky-scroll .bom-table thead th.col-actions { top: 0; z-index: 3; background: var(--paper-2); }
  /* rgba(181,56,49,.05) 叠在白底上的实色 */
  .bom-sticky-scroll .bom-table tr.has-issue > td.bom-part,
  .bom-sticky-scroll .bom-table tr.has-issue > td.bom-row-actions { background: #fbf5f5; }
  .bom-sticky-scroll .bom-table tr.ai-located > td.bom-part,
  .bom-sticky-scroll .bom-table tr.ai-located > td.bom-row-actions {
    background: #fff0bb; box-shadow: inset 0 2px #b77900, inset 0 -2px #b77900;
  }
  .bom-sticky-scroll .bom-table tr.bom-quick-row > td.bom-part,
  .bom-sticky-scroll .bom-table tr.bom-quick-row > td.bom-row-actions { background: #fbfcfe; }
}
</style>
